import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { BriefcaseBusiness, CircleUserRound, FilePlus2, FolderKanban, Inbox, Info, RefreshCw } from "lucide-react";
import config from "../../config/config";
import CockpitShell from "../../components/CockpitShell";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import StbAuthorityFilingEmployerWorkspace from "./StbAuthorityFilingEmployerWorkspace";

const navItems = [
  { href: "/dashboard/employer", label: "Overview", icon: BriefcaseBusiness },
  { href: "/dashboard/stb/create", label: "Neue Anzeige", icon: FilePlus2 },
  { href: "/dashboard/stb/manage", label: "Verwalten", icon: FolderKanban },
  { href: "/dashboard/stb/applications", label: "Bewerbungen", icon: Inbox },
  { href: "/dashboard/account", label: "Account", icon: CircleUserRound },
];

const DECISION_STATE_OPTIONS = [
  { value: "new", label: "Neu" },
  { value: "review", label: "In Prüfung" },
  { value: "shortlist", label: "Interessant" },
  { value: "rejected", label: "Abgelehnt" },
  { value: "hired", label: "Eingestellt" },
];

const VERIFICATION_STATUS_LABELS = {
  declared: "Declared",
  uploaded: "Uploaded",
  pending_review: "Pending Review",
  formally_checked: "Checked",
  online_verifiable: "Online Verifiable",
  online_verified: "Online Verified",
  inconsistent: "Inconsistent",
  suspicious: "Suspicious",
  rejected: "Rejected",
};

const FEDERAL_STATE_OPTIONS = [
  "Baden-Wuerttemberg",
  "Bayern",
  "Berlin",
  "Brandenburg",
  "Bremen",
  "Hamburg",
  "Hessen",
  "Mecklenburg-Vorpommern",
  "Niedersachsen",
  "Nordrhein-Westfalen",
  "Rheinland-Pfalz",
  "Saarland",
  "Sachsen",
  "Sachsen-Anhalt",
  "Schleswig-Holstein",
  "Thueringen",
];

const RECOGNITION_STATUS_OPTIONS = [
  { value: "required", label: "Anerkennung erforderlich" },
  { value: "open", label: "Anerkennung offen" },
  { value: "partial", label: "Anerkennung teilweise" },
  { value: "full", label: "Anerkennung voll" },
  { value: "partnership", label: "Anerkennungspartnerschaft" },
  { value: "not_required", label: "Keine Anerkennungspflicht" },
];

const REGULATED_PROFESSION_OPTIONS = [
  { value: "auto", label: "Automatisch erkennen" },
  { value: "1", label: "Ja, reglementiert" },
  { value: "0", label: "Nein, nicht reglementiert" },
];

const PROCEDURE_OPTIONS = [
  { value: "0", label: "Standardverfahren" },
  { value: "1", label: "Beschleunigtes Fachkraefteverfahren" },
];

const FILING_FIELD_HELP = {
  procedure: {
    title: "Verfahrensart",
    body: "Dieses Feld entscheidet, wie aktiv der Arbeitgeber den Einwanderungspfad steuert.",
    points: [
      "Standardverfahren: regulaerer Anerkennungs-, BA- und Visumpfad ohne fruehe Arbeitgebersteuerung.",
      "Beschleunigtes Verfahren: fruehe Einbindung der Auslaenderbehoerde, oft mit Vollmacht und zusaetzlichen Arbeitgeberunterlagen.",
      "Folge: Experts4Europe zieht andere Stellen, Unterlagen und Signaturpfade in das Paket.",
    ],
  },
  regulated: {
    title: "Reglementierter Beruf",
    body: "Hier wird entschieden, ob fuer die Berufsausuebung eine gesetzliche Anerkennung oder Berufserlaubnis noetig ist.",
    points: [
      "Relevant bei Pflege, Physiotherapie und anderen gesetzlich geschuetzten Berufswegen.",
      "Folge: Landes- oder kommunale Anerkennungsstellen und Zusatzunterlagen werden zwingender Teil des Pakets.",
      "Nicht reglementierte Berufe laufen staerker ueber Arbeitgeber-, BA- und Visumpfade wie Blue Card oder qualifizierte Beschaeftigung.",
    ],
  },
  state: {
    title: "Bundesland und lokale Zustaendigkeit",
    body: "Deutschland arbeitet mit Laenderhoheit. Bundesland und Zielort koennen Verfahren, Stellen und Formulare spuerbar veraendern.",
    points: [
      "Dasselbe Berufsbild kann je Land ueber andere Anerkennungsstellen oder Formulare laufen.",
      "Kommunale Sonderstellen wie Muenchen, Frankfurt oder Hamburg koennen den Landesdefault nochmals ueberlagern.",
      "Folge: Experts4Europe loest Bund, Land und Stadt getrennt auf und markiert aktive Overrides sichtbar.",
    ],
  },
  recognition: {
    title: "Anerkennungsstatus",
    body: "Der Anerkennungsstatus zeigt, wie weit der berufliche Zugang bereits geklaert ist und wie viel Anerkennungsarbeit noch offen bleibt.",
    points: [
      "Voll anerkannt: weniger Anerkennungsdokumente, mehr Fokus auf Beschaeftigung und Visum.",
      "Offen oder teilweise: mehr Nachweise, Pruefpfade und Anerkennungsunterlagen.",
      "Anerkennungspartnerschaft: zusaetzliche Arbeitgeberunterlagen und Qualifizierungspfad moeglich.",
    ],
  },
};

const REVIEW_BUCKET_META = {
  ready_to_sign: {
    label: "Ready to sign",
    tone: "border-emerald-200 bg-emerald-50 text-emerald-700",
  },
  ready_to_upload: {
    label: "Ready to upload",
    tone: "border-sky-200 bg-sky-50 text-sky-700",
  },
  prepared_but_incomplete: {
    label: "Prepared, but incomplete",
    tone: "border-amber-200 bg-amber-50 text-amber-700",
  },
  manual_only: {
    label: "Manual only",
    tone: "border-slate-200 bg-slate-100 text-slate-700",
  },
  missing_docs: {
    label: "Missing docs",
    tone: "border-rose-200 bg-rose-50 text-rose-700",
  },
};

const FRESHNESS_META = {
  current_locally_checked: {
    tone: "border-emerald-200 bg-emerald-50 text-emerald-700",
  },
  local_unknown_currency: {
    tone: "border-amber-200 bg-amber-50 text-amber-700",
  },
  local_update_unclear: {
    tone: "border-amber-200 bg-amber-50 text-amber-700",
  },
  officially_described_only: {
    tone: "border-slate-200 bg-slate-100 text-slate-700",
  },
  reference_only: {
    tone: "border-slate-200 bg-slate-100 text-slate-700",
  },
};

const RESPONSIBILITY_META = {
  employer: {
    tone: "border-sky-200 bg-sky-50 text-sky-700",
  },
  candidate: {
    tone: "border-emerald-200 bg-emerald-50 text-emerald-700",
  },
  both: {
    tone: "border-violet-200 bg-violet-50 text-violet-700",
  },
  third_party: {
    tone: "border-amber-200 bg-amber-50 text-amber-700",
  },
  authority: {
    tone: "border-slate-200 bg-slate-100 text-slate-700",
  },
  manual: {
    tone: "border-slate-200 bg-slate-100 text-slate-700",
  },
};

const TASK_STATUS_LABELS = {
  open: "Offen",
  requested: "Angefragt",
  in_progress: "In Arbeit",
  completed: "Erledigt",
  blocked: "Blockiert",
  waived: "Zurueckgestellt",
};

const REQUEST_STATUS_LABELS = {
  draft: "Entwurf",
  sent: "Gesendet",
  viewed: "Gesehen",
  answered: "Beantwortet",
  fulfilled: "Erfuellt",
  expired: "Abgelaufen",
  obsolete: "Obsolet",
};

const RESPONSE_STATUS_LABELS = {
  response_received: "Antwort eingegangen",
  response_under_review: "In Review",
  response_accepted: "Antwort akzeptiert",
  response_rejected: "Nacharbeit noetig",
  response_incomplete: "Antwort unvollstaendig",
};

function authHeaders() {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function formatDecisionState(value) {
  return DECISION_STATE_OPTIONS.find((option) => option.value === value)?.label || value || "Neu";
}

function formatBoolean(value) {
  return value ? "Ja" : "Nein";
}

function formatResponseStatus(value) {
  return RESPONSE_STATUS_LABELS[String(value || "").toLowerCase()] || "Noch keine Antwort";
}

function formatDateTime(value) {
  if (!value) return "n/a";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" });
}

function formatRelativeDays(value) {
  if (value == null || value === "") return "n/a";
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return String(value);
  if (numeric === 0) return "heute";
  if (numeric === 1) return "1 Tag";
  if (numeric > 1) return `${numeric} Tage`;
  if (numeric === -1) return "seit 1 Tag ueberfaellig";
  return `seit ${Math.abs(numeric)} Tagen ueberfaellig`;
}

function formatUrgencyLabel(value) {
  const normalized = String(value || "").toLowerCase();
  if (normalized === "critical" || normalized === "urgent") return "kritisch";
  if (normalized === "high") return "hoch";
  if (normalized === "low") return "niedrig";
  return "mittel";
}

function getDeadlineTone(requestEntry) {
  if (requestEntry?.overdue) return "border-rose-200 bg-rose-50 text-rose-700";
  if (requestEntry?.due_soon) return "border-amber-200 bg-amber-50 text-amber-700";
  if (requestEntry?.fresh_response) return "border-sky-200 bg-sky-50 text-sky-700";
  return "border-slate-200 bg-slate-50 text-slate-600";
}

function buildInitialDecisionForm(detail) {
  return {
    decision_state: detail?.decision_preparation?.decision_state || detail?.application_status || "new",
    decision_note: detail?.decision_preparation?.decision_note || "",
  };
}

function detectDefaultFederalState(selectedAd) {
  if (selectedAd?.standort_ort === "Berlin") return "Berlin";
  if (selectedAd?.standort_ort === "Muenchen" || selectedAd?.standort_ort === "München") return "Bayern";
  return "";
}

function detectInitialRecognitionStatus(selectedAd) {
  const professionText = String(selectedAd?.berufsbezeichnung || selectedAd?.titel || selectedAd?.standort_ort || "").toLowerCase();
  if (/\bit\b|software|entwickler|developer|engineer/.test(professionText)) return "not_required";
  return "required";
}

function buildInitialFilingForm(selectedAd) {
  return {
    federal_state: detectDefaultFederalState(selectedAd),
    city: selectedAd?.standort_ort || "",
    recognition_status: detectInitialRecognitionStatus(selectedAd),
    fast_track: "0",
    regulated_profession: "auto",
  };
}

function createEmptyDetailState(selectedAd = null) {
  return {
    data: null,
    form: { decision_state: "new", decision_note: "" },
    filingForm: buildInitialFilingForm(selectedAd),
    filingData: null,
    filingError: "",
    filingFlash: "",
    filingLoading: false,
    refreshingDocuments: false,
    exporting: false,
    finalizingPack: false,
    taskNotes: {},
    error: "",
    flash: "",
    loading: false,
    saving: false,
  };
}

function formatVerificationSummary(byStatus) {
  const entries = Object.entries(byStatus || {}).filter(([, count]) => Number(count || 0) > 0);
  if (!entries.length) return "Nur Kompaktstatus, keine Rohsicht auf Referenzen.";
  return entries
    .map(([status, count]) => `${VERIFICATION_STATUS_LABELS[status] || status}: ${count}`)
    .join(" · ");
}

function buildFilingQuery(form) {
  const params = new URLSearchParams();
  if (form.federal_state) params.set("federal_state", form.federal_state);
  if (form.city) params.set("city", form.city);
  if (form.recognition_status) params.set("recognition_status", form.recognition_status);
  if (form.fast_track) params.set("fast_track", form.fast_track);
  if (form.regulated_profession && form.regulated_profession !== "auto") {
    params.set("regulated_profession", form.regulated_profession);
  }
  return params.toString();
}

function makeDownloadName(prefix, extension) {
  const stamp = new Date().toISOString().slice(0, 10);
  return `${prefix}-${stamp}.${extension}`;
}

function FilingInfoHint({ hintKey }) {
  const content = FILING_FIELD_HELP[hintKey];
  if (!content) return null;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-500 transition hover:border-slate-400 hover:text-slate-700"
          title={content.title}
        >
          <Info className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80">
        <div className="space-y-2">
          <p className="text-sm font-semibold text-slate-900">{content.title}</p>
          <p className="text-sm leading-6 text-slate-600">{content.body}</p>
          {content.points?.length ? (
            <ul className="list-disc space-y-1 pl-5 text-sm leading-6 text-slate-600">
              {content.points.map((entry) => (
                <li key={entry}>{entry}</li>
              ))}
            </ul>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function getReviewBucketMeta(bucket) {
  return REVIEW_BUCKET_META[bucket] || {
    label: bucket || "Unbekannt",
    tone: "border-slate-200 bg-slate-100 text-slate-700",
  };
}

function getFreshnessMeta(status) {
  return FRESHNESS_META[status] || {
    tone: "border-slate-200 bg-slate-100 text-slate-700",
  };
}

function formatCoverageLevel(value) {
  const labels = {
    high: "hohe Feldabdeckung",
    medium: "mittlere Feldabdeckung",
    low: "niedrige Feldabdeckung",
    none: "keine Feldabdeckung",
  };
  return labels[value] || value || "unbekannt";
}

function formatDelta(value) {
  const numeric = Number(value || 0);
  return `${numeric >= 0 ? "+" : ""}${numeric}`;
}

function formatHistoryAction(actionType) {
  const labels = {
    generated: "Kit erzeugt",
    export_json: "JSON exportiert",
    export_html: "HTML exportiert",
    document_refresh: "Bibliothek aktualisiert",
    task_complete: "Task erledigt",
    task_block: "Task blockiert",
    task_reopen: "Task wieder geoeffnet",
    task_request: "Task als Request markiert",
    task_progress: "Task in Arbeit",
    task_note: "Task-Notiz gespeichert",
    request_created: "Request erstellt",
    request_sent: "Request gesendet",
    request_viewed: "Request gesehen",
    request_answered: "Request beantwortet",
    request_fulfilled: "Request erfuellt",
    request_obsolete: "Request abgeschlossen",
    finalized_pack: "Final Pack erzeugt",
  };
  return labels[actionType] || actionType || "Aktion";
}

function formatTaskStatus(value) {
  return TASK_STATUS_LABELS[value] || value || "Offen";
}

function formatRequestStatus(value) {
  return REQUEST_STATUS_LABELS[value] || value || "Entwurf";
}

function getResponsibilityMeta(key) {
  return RESPONSIBILITY_META[key] || {
    tone: "border-slate-200 bg-slate-100 text-slate-700",
  };
}

const StbApplications = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [ads, setAds] = useState([]);
  const [adId, setAdId] = useState(searchParams.get("adId") || "");
  const requestedApplicationId = searchParams.get("applicationId") || "";
  const requestedRequestId = searchParams.get("request_id") || "";
  const requestedFilingView = searchParams.get("filing_view") || "";
  const [rows, setRows] = useState([]);
  const [message, setMessage] = useState("");
  const [expandedId, setExpandedId] = useState("");
  const [detailsById, setDetailsById] = useState({});
  const [filingTabsById, setFilingTabsById] = useState({});
  const selectedAd = ads.find((ad) => String(ad.id) === String(adId)) || null;

  const updateDetailState = (applicationId, updater) => {
    const key = String(applicationId);
    setDetailsById((current) => {
      const previous = current[key] || createEmptyDetailState(selectedAd);
      const next = typeof updater === "function" ? updater(previous) : { ...previous, ...updater };
      return { ...current, [key]: next };
    });
  };

  const getActiveFilingTab = (applicationId) => (
    filingTabsById[String(applicationId)]
    || ((["candidate", "documents", "pack", "history"].includes(requestedFilingView) && String(requestedApplicationId) === String(applicationId)) ? requestedFilingView : "candidate")
    || ((requestedRequestId && String(requestedApplicationId) === String(applicationId)) ? "candidate" : "candidate")
  );

  const setActiveFilingTab = (applicationId, nextTab) => {
    setFilingTabsById((current) => ({
      ...current,
      [String(applicationId)]: nextTab,
    }));
  };

  const loadAds = async () => {
    const res = await axios.get(`${config.API_BASE_URL}/api/stb/employer/ads`, {
      params: { page: 1, limit: 100 },
      headers: authHeaders(),
    });
    const list = res.data?.rows || [];
    setAds(list);
    if (!adId && list[0]?.id) setAdId(String(list[0].id));
  };

  const loadApplications = async (id) => {
    if (!id) return;
    try {
      const res = await axios.get(`${config.API_BASE_URL}/api/stb/employer/ads/${id}/applications`, {
        headers: authHeaders(),
      });
      setRows(res.data?.rows || []);
      setExpandedId("");
      setDetailsById({});
      setMessage("");
    } catch (e) {
      setMessage(e.response?.data?.message || "Bewerbungen konnten nicht geladen werden.");
      setRows([]);
      setExpandedId("");
      setDetailsById({});
    }
  };

  const loadDecisionSummary = async (applicationId) => {
    updateDetailState(applicationId, (previous) => ({
      ...previous,
      loading: true,
      error: "",
      flash: "",
    }));
    try {
      const res = await axios.get(
        `${config.API_BASE_URL}/api/stb/employer/applications/${applicationId}/decision-handoff`,
        { headers: authHeaders() }
      );
      updateDetailState(applicationId, {
        data: res.data,
        form: buildInitialDecisionForm(res.data),
        filingForm: (detailsById[String(applicationId)]?.filingForm) || buildInitialFilingForm(selectedAd),
        loading: false,
        saving: false,
        error: "",
        flash: "",
      });
    } catch (e) {
      updateDetailState(applicationId, (previous) => ({
        ...previous,
        loading: false,
        error: e.response?.data?.message || "Decision Summary konnte nicht geladen werden.",
      }));
    }
  };

  const toggleDecisionSummary = async (applicationId) => {
    const key = String(applicationId);
    if (expandedId === key) {
      setExpandedId("");
      return;
    }

    setExpandedId(key);
    const current = detailsById[key];
    if (!current?.data && !current?.loading) {
      await loadDecisionSummary(applicationId);
    }
  };

  const handleDecisionFormChange = (applicationId, field, value) => {
    updateDetailState(applicationId, (previous) => ({
      ...previous,
      form: {
        ...previous.form,
        [field]: value,
      },
      flash: "",
      error: "",
    }));
  };

  const handleFilingFormChange = (applicationId, field, value) => {
    updateDetailState(applicationId, (previous) => ({
      ...previous,
      filingForm: {
        ...previous.filingForm,
        [field]: value,
      },
      filingFlash: "",
      filingError: "",
    }));
  };

  const handleTaskNoteChange = (applicationId, taskId, value) => {
    updateDetailState(applicationId, (previous) => ({
      ...previous,
      taskNotes: {
        ...previous.taskNotes,
        [String(taskId)]: value,
      },
    }));
  };

  const loadAuthorityFilingKit = async (applicationId) => {
    const key = String(applicationId);
    const current = detailsById[key] || createEmptyDetailState(selectedAd);
    updateDetailState(applicationId, (previous) => ({
      ...previous,
      filingLoading: true,
      filingError: "",
      filingFlash: "",
    }));
    try {
      const query = buildFilingQuery(current.filingForm);
      const res = await axios.get(
        `${config.API_BASE_URL}/api/stb/employer/applications/${applicationId}/authority-filing-kit${query ? `?${query}` : ""}`,
        { headers: authHeaders() }
      );
      updateDetailState(applicationId, (previous) => ({
        ...previous,
        filingData: res.data,
        filingLoading: false,
        filingFlash: "Authority Filing Kit aktualisiert.",
      }));
    } catch (e) {
      updateDetailState(applicationId, (previous) => ({
        ...previous,
        filingLoading: false,
        filingError: e.response?.data?.message || "Authority Filing Kit konnte nicht geladen werden.",
      }));
    }
  };

  const downloadBlob = async (applicationId, url, filename) => {
    updateDetailState(applicationId, (previous) => ({
      ...previous,
      exporting: true,
      filingError: "",
    }));
    try {
      const res = await axios.get(url, {
        headers: authHeaders(),
        responseType: "blob",
      });
      const objectUrl = window.URL.createObjectURL(new Blob([res.data]));
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(objectUrl);
      updateDetailState(applicationId, (previous) => ({
        ...previous,
        exporting: false,
        filingFlash: `Download erstellt: ${filename}`,
      }));
    } catch (e) {
      updateDetailState(applicationId, (previous) => ({
        ...previous,
        exporting: false,
        filingError: e.response?.data?.message || "Download fehlgeschlagen.",
      }));
    }
  };

  const exportAuthorityFilingKit = async (applicationId, format) => {
    const key = String(applicationId);
    const current = detailsById[key] || createEmptyDetailState(selectedAd);
    const query = buildFilingQuery(current.filingForm);
    const suffix = format === "html" ? "html" : "json";
    const url = `${config.API_BASE_URL}/api/stb/employer/applications/${applicationId}/authority-filing-kit/export?format=${format}${query ? `&${query}` : ""}`;
    await downloadBlob(applicationId, url, makeDownloadName(`authority-filing-kit-${applicationId}`, suffix));
  };

  const downloadAuthorityDocument = async (applicationId, documentEntry) => {
    if (!documentEntry?.download_url) return;
    const filename = documentEntry.local_path?.split("/").pop() || `${documentEntry.id}.pdf`;
    await downloadBlob(applicationId, `${config.API_BASE_URL}${documentEntry.download_url}`, filename);
  };

  const downloadPreparedAsset = async (applicationId, assetEntry) => {
    if (!assetEntry?.download_url) return;
    await downloadBlob(applicationId, `${config.API_BASE_URL}${assetEntry.download_url}`, assetEntry.file_name || `${assetEntry.asset_id}.${assetEntry.format || "bin"}`);
  };

  const applyWorkflowKitResponse = (applicationId, kit, flashMessage) => {
    updateDetailState(applicationId, (previous) => ({
      ...previous,
      filingData: kit,
      filingLoading: false,
      refreshingDocuments: false,
      finalizingPack: false,
      filingFlash: flashMessage || previous.filingFlash,
      filingError: "",
    }));
  };

  const updateAuthorityTask = async (applicationId, task, action) => {
    const key = String(applicationId);
    const current = detailsById[key] || createEmptyDetailState(selectedAd);
    const query = buildFilingQuery(current.filingForm);

    updateDetailState(applicationId, (previous) => ({
      ...previous,
      filingLoading: true,
      filingError: "",
      filingFlash: "",
    }));

    try {
      const res = await axios.patch(
        `${config.API_BASE_URL}/api/stb/employer/applications/${applicationId}/authority-filing-kit/tasks/${task.task_id}${query ? `?${query}` : ""}`,
        {
          action,
          completion_note: current.taskNotes?.[String(task.task_id)] || task.completion_note || "",
        },
        { headers: authHeaders() }
      );
      applyWorkflowKitResponse(applicationId, res.data?.kit, `Task aktualisiert: ${task.title}`);
    } catch (e) {
      updateDetailState(applicationId, (previous) => ({
        ...previous,
        filingLoading: false,
        filingError: e.response?.data?.message || "Task konnte nicht aktualisiert werden.",
      }));
    }
  };

  const createCandidateRequest = async (applicationId, task, sendNow = true) => {
    const key = String(applicationId);
    const current = detailsById[key] || createEmptyDetailState(selectedAd);
    const query = buildFilingQuery(current.filingForm);

    updateDetailState(applicationId, (previous) => ({
      ...previous,
      filingLoading: true,
      filingError: "",
      filingFlash: "",
    }));

    try {
      const res = await axios.post(
        `${config.API_BASE_URL}/api/stb/employer/applications/${applicationId}/authority-filing-kit/requests${query ? `?${query}` : ""}`,
        {
          task_id: task.task_id,
          document_id: task.document_id,
          title: `Request: ${task.title}`,
          description: task.description || "Bitte fehlende Angabe oder Unterlage fuer das Filing Kit bereitstellen.",
          request_reason: task.description || task.title,
          requirement_kind: task.source_type,
          urgency: task.priority || "medium",
          requires_upload: task.requires_upload || task.requires_candidate_input,
          requires_signature: task.requires_signature,
          due_at: task.due_at || null,
          status: sendNow ? "sent" : "draft",
          send_now: sendNow,
        },
        { headers: authHeaders() }
      );
      applyWorkflowKitResponse(applicationId, res.data?.kit, `Request gespeichert: ${task.title}`);
    } catch (e) {
      updateDetailState(applicationId, (previous) => ({
        ...previous,
        filingLoading: false,
        filingError: e.response?.data?.message || "Candidate-Request konnte nicht erstellt werden.",
      }));
    }
  };

  const updateCandidateRequest = async (applicationId, requestEntry, status, extra = {}) => {
    const key = String(applicationId);
    const current = detailsById[key] || createEmptyDetailState(selectedAd);
    const query = buildFilingQuery(current.filingForm);

    updateDetailState(applicationId, (previous) => ({
      ...previous,
      filingLoading: true,
      filingError: "",
      filingFlash: "",
    }));

    try {
      const res = await axios.patch(
        `${config.API_BASE_URL}/api/stb/employer/applications/${applicationId}/authority-filing-kit/requests/${requestEntry.request_id}${query ? `?${query}` : ""}`,
        { status, ...extra },
        { headers: authHeaders() }
      );
      applyWorkflowKitResponse(applicationId, res.data?.kit, `Request aktualisiert: ${requestEntry.title}`);
    } catch (e) {
      updateDetailState(applicationId, (previous) => ({
        ...previous,
        filingLoading: false,
        filingError: e.response?.data?.message || "Request konnte nicht aktualisiert werden.",
      }));
    }
  };

  const finalizeSubmissionPack = async (applicationId) => {
    const key = String(applicationId);
    const current = detailsById[key] || createEmptyDetailState(selectedAd);
    const query = buildFilingQuery(current.filingForm);

    updateDetailState(applicationId, (previous) => ({
      ...previous,
      finalizingPack: true,
      filingError: "",
      filingFlash: "",
    }));

    try {
      const res = await axios.post(
        `${config.API_BASE_URL}/api/stb/employer/applications/${applicationId}/authority-filing-kit/finalize${query ? `?${query}` : ""}`,
        {},
        { headers: authHeaders() }
      );
      applyWorkflowKitResponse(applicationId, res.data?.kit, "Final Submission Pack erzeugt.");
    } catch (e) {
      updateDetailState(applicationId, (previous) => ({
        ...previous,
        finalizingPack: false,
        filingError: e.response?.data?.message || "Final Submission Pack konnte nicht erzeugt werden.",
      }));
    }
  };

  const refreshAuthorityDocuments = async (applicationId) => {
    const key = String(applicationId);
    const current = detailsById[key] || createEmptyDetailState(selectedAd);
    const query = buildFilingQuery(current.filingForm);

    updateDetailState(applicationId, (previous) => ({
      ...previous,
      refreshingDocuments: true,
      filingError: "",
      filingFlash: "",
    }));

    try {
      const res = await axios.post(
        `${config.API_BASE_URL}/api/stb/employer/applications/${applicationId}/authority-filing-kit/refresh-documents${query ? `?${query}` : ""}`,
        {},
        { headers: authHeaders() }
      );
      updateDetailState(applicationId, (previous) => ({
        ...previous,
        refreshingDocuments: false,
        filingData: res.data,
        filingFlash: `Offizielle Dokumente aktualisiert: ${res.data?.document_refresh?.updated_count || 0} Dateien neu gezogen.`,
      }));
    } catch (e) {
      updateDetailState(applicationId, (previous) => ({
        ...previous,
        refreshingDocuments: false,
        filingError: e.response?.data?.message || "Dokumentenbibliothek konnte nicht aktualisiert werden.",
      }));
    }
  };

  const saveDecisionPreparation = async (applicationId) => {
    const key = String(applicationId);
    const current = detailsById[key] || createEmptyDetailState();
    updateDetailState(applicationId, (previous) => ({
      ...previous,
      saving: true,
      error: "",
      flash: "",
    }));

    try {
      const res = await axios.patch(
        `${config.API_BASE_URL}/api/stb/employer/applications/${applicationId}/decision-preparation`,
        current.form,
        { headers: authHeaders() }
      );

      const updated = res.data;
      setRows((currentRows) => currentRows.map((row) => (
        row.id === applicationId
          ? { ...row, status: updated.application_status }
          : row
      )));
      updateDetailState(applicationId, {
        data: updated,
        form: buildInitialDecisionForm(updated),
        loading: false,
        saving: false,
        error: "",
        flash: "Interne Entscheidungsvorbereitung gespeichert.",
      });
    } catch (e) {
      updateDetailState(applicationId, (previous) => ({
        ...previous,
        saving: false,
        error: e.response?.data?.message || "Decision Preparation konnte nicht gespeichert werden.",
      }));
    }
  };

  useEffect(() => {
    document.title = "Experts4Europe | Employer Bewerbungen";
    loadAds().catch(() => setMessage("Anzeigen konnten nicht geladen werden."));
  }, []);

  useEffect(() => {
    if (!adId) return;
    loadApplications(adId);
    const next = new URLSearchParams();
    next.set("adId", adId);
    if (requestedApplicationId) next.set("applicationId", requestedApplicationId);
    if (requestedRequestId) next.set("request_id", requestedRequestId);
    if (requestedFilingView) next.set("filing_view", requestedFilingView);
    setSearchParams(next);
  }, [adId, requestedApplicationId, requestedRequestId, requestedFilingView, setSearchParams]);

  useEffect(() => {
    if (!requestedApplicationId) return;
    if (!rows.some((entry) => String(entry.id) === String(requestedApplicationId))) return;
    setExpandedId(String(requestedApplicationId));
    const detail = detailsById[String(requestedApplicationId)];
    if (!detail?.data && !detail?.loading) {
      loadDecisionSummary(Number(requestedApplicationId));
    }
    if (!detail?.filingData && !detail?.filingLoading) {
      loadAuthorityFilingKit(Number(requestedApplicationId));
    }
  }, [requestedApplicationId, rows, detailsById]);

  const employerNotifications = React.useMemo(() => (
    Object.entries(detailsById || {})
      .flatMap(([applicationId, entry]) => (
        (entry?.filingData?.notification_board?.employer_notifications || []).map((notification) => ({
          ...notification,
          application_id: applicationId,
          ad_id: adId,
        }))
      ))
      .sort((left, right) => new Date(right.created_at || 0).getTime() - new Date(left.created_at || 0).getTime())
      .slice(0, 12)
      .map((entry) => ({
        key: `employer-filing-${entry.notification_id}`,
        title: entry.title || "Filing-Hinweis",
        text: entry.message || "",
        href: `/dashboard/stb/applications?adId=${encodeURIComponent(entry.ad_id || adId || "")}&applicationId=${encodeURIComponent(entry.application_id || "")}&request_id=${encodeURIComponent(entry.request_id || "")}`,
      }))
  ), [adId, detailsById]);

  return (
    <CockpitShell
      title="Bewerbungen mit Decision Summary"
      subtitle="Die Bewerbungsansicht bleibt Teil desselben Hiring Control Decks und fuehrt direkt in die kompakte Decision Summary."
      badge="Employer Applications"
      navItems={navItems}
      wideLayout
      theme="employer"
      shellLabel="Applications"
      shellDescription="Folgeflaeche im selben Employer-Template fuer Bewerbungen, Decision Summary und interne Entscheidungsvorbereitung."
      modeLabel="Decision Flow"
      liveLabel="Employer"
      topSignal="Employer System"
      topContext="Bewerbungen"
      notifications={employerNotifications}
      actions={(
        <Button
          variant="outline"
          className="dashboard-action-button dashboard-action-button-secondary"
          onClick={() => loadApplications(adId)}
        >
          Refresh
        </Button>
      )}
    >
      {message ? (
        <p className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
          {message}
        </p>
      ) : null}

      <Card className="dashboard-card-shell">
        <CardHeader>
          <CardTitle className="dashboard-card-title">Anzeige auswaehlen</CardTitle>
        </CardHeader>
        <CardContent>
          <Select value={adId} onValueChange={setAdId}>
            <SelectTrigger className="dashboard-field max-w-xl">
              <SelectValue placeholder="Anzeige waehlen" />
            </SelectTrigger>
            <SelectContent>
              {ads.map((ad) => (
                <SelectItem key={ad.id} value={String(ad.id)}>
                  #{ad.id} · {ad.titel} · {ad.status}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card className="dashboard-card-shell">
        <CardHeader>
          <CardTitle className="dashboard-card-title">Anonyme Bewerbungen</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-slate-600">
          {rows.length === 0 ? <p className="text-slate-500">Keine Bewerbungen vorhanden.</p> : null}
          {rows.map((row) => {
            const detailState = detailsById[String(row.id)] || createEmptyDetailState();
            const detail = detailState.data;
            const candidateCore = detail?.decision_handoff?.candidate_core || {};
            const verification = detail?.decision_handoff?.verification?.references || {};
            const languageSignal = detail?.decision_handoff?.language_test_b1_core || { shared: false };
            const wtSummary = detail?.decision_handoff?.wt_summary || { shared: false };
            const filingData = detailState.filingData;

            return (
              <div key={row.id} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div className="space-y-1">
                    <p className="text-base font-semibold text-slate-900">
                      Bewerbung #{row.id} · {row.candidate_ref || "CAND-pending"}
                    </p>
                    <p className="text-xs uppercase tracking-[0.18em] text-slate-400">
                      Aktueller Status: {formatDecisionState(row.status)}
                    </p>
                    {row.wt_shared && row.wt_summary ? (
                      <p className="text-sky-700">
                        Wissenstest geteilt: {row.wt_summary.score_percent}% ({row.wt_summary.correct_count}/{row.wt_summary.total_count})
                      </p>
                    ) : (
                      <p className="text-slate-400">Kein geteilter Wissenstest.</p>
                    )}
                  </div>

                  <Button
                    variant="outline"
                    className="dashboard-inline-button"
                    onClick={() => toggleDecisionSummary(row.id)}
                  >
                    {expandedId === String(row.id) ? "Summary ausblenden" : "Decision Summary"}
                  </Button>
                </div>

                {expandedId === String(row.id) ? (
                  <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4">
                    {detailState.loading ? <p className="text-slate-500">Decision Summary wird geladen...</p> : null}
                    {detailState.error ? (
                      <p className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
                        {detailState.error}
                      </p>
                    ) : null}

                    {detail ? (
                      <div className="space-y-4">
                        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                            <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Application</p>
                            <p className="mt-2 text-slate-900">#{detail.application_id}</p>
                            <p className="mt-1 text-slate-600">{detail.candidate_ref || "CAND-pending"}</p>
                            <p className="mt-1 text-sky-700">{formatDecisionState(detail.application_status)}</p>
                          </div>

                          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                            <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Candidate Core</p>
                            <p className="mt-2 text-slate-700">Profilbasis: {formatBoolean(candidateCore.profile_basis_ready)}</p>
                            <p className="text-slate-700">CV-Kern: {formatBoolean(candidateCore.cv_core_ready)}</p>
                            <p className="mt-2 text-slate-600">
                              Skills {candidateCore.skills?.count || 0} · Sprachen {candidateCore.languages?.count || 0}
                            </p>
                            <p className="text-slate-600">
                              Ausbildung {candidateCore.education?.count || 0} · Erfahrung {candidateCore.experience?.count || 0}
                            </p>
                          </div>

                          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                            <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Verification</p>
                            <p className="mt-2 text-slate-700">Nachweise vorhanden: {formatBoolean(verification.present)}</p>
                            <p className="text-slate-700">Referenzen gesamt: {verification.count || 0}</p>
                            <p className="mt-2 text-slate-600">{formatVerificationSummary(verification.by_status)}</p>
                          </div>

                          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                            <p className="text-xs uppercase tracking-[0.18em] text-slate-400">WT Summary</p>
                            {wtSummary.shared && wtSummary.summary ? (
                              <>
                                <p className="mt-2 text-slate-700">Geteilt: Ja</p>
                                <p className="text-sky-700">
                                  {wtSummary.summary.score_percent}% ({wtSummary.summary.correct_count}/{wtSummary.summary.total_count})
                                </p>
                              </>
                            ) : (
                              <>
                                <p className="mt-2 text-slate-700">Geteilt: Nein</p>
                                <p className="text-slate-600">WT bleibt optionales Zusatzsignal.</p>
                              </>
                            )}
                          </div>
                        </div>

                        <div className="rounded-lg border border-emerald-200 bg-emerald-50/70 p-4">
                          <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Language Test B1 Core</p>
                          {languageSignal.shared && languageSignal.signal ? (
                            <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                              <div className="rounded-lg border border-slate-200 bg-white p-3">
                                <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Band</p>
                                <p className="mt-2 text-xl font-semibold text-slate-900">{languageSignal.signal.estimated_cefr_band || "n/a"}</p>
                                <p className="mt-1 text-slate-600">Overall {languageSignal.signal.overall_language_score || "n/a"}</p>
                              </div>
                              <div className="rounded-lg border border-slate-200 bg-white p-3">
                                <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Test</p>
                                <p className="mt-2 text-slate-900">{languageSignal.signal.test_type || "n/a"}</p>
                                <p className="mt-1 text-slate-600">Review {languageSignal.signal.review_status || "n/a"}</p>
                              </div>
                              <div className="rounded-lg border border-slate-200 bg-white p-3">
                                <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Progress</p>
                                <p className="mt-2 text-slate-900">{languageSignal.signal.progress?.trend || "baseline"}</p>
                                <p className="mt-1 text-slate-600">Delta {languageSignal.signal.progress?.delta ?? "n/a"}</p>
                              </div>
                              <div className="rounded-lg border border-slate-200 bg-white p-3">
                                <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Module</p>
                                <p className="mt-2 text-slate-600">
                                  Lesen {languageSignal.signal.module_scores?.reading ?? "n/a"} · Hoeren {languageSignal.signal.module_scores?.listening ?? "n/a"}
                                </p>
                                <p className="text-slate-600">
                                  Bausteine {languageSignal.signal.module_scores?.language_elements ?? "n/a"} · Schreiben {languageSignal.signal.module_scores?.writing ?? "n/a"} · Sprechen {languageSignal.signal.module_scores?.speaking ?? "n/a"}
                                </p>
                              </div>
                              <div className="rounded-lg border border-slate-200 bg-white p-3 md:col-span-2 xl:col-span-4">
                                <p className="text-slate-700">{languageSignal.signal.summary?.summary || "Keine kompakte Sprachsummary vorhanden."}</p>
                                {languageSignal.signal.summary?.caution ? (
                                  <p className="mt-2 text-amber-700">{languageSignal.signal.summary.caution}</p>
                                ) : null}
                              </div>
                            </div>
                          ) : (
                            <p className="mt-3 text-slate-600">
                              Kein freigegebenes B1-Core-Sprachsignal vorhanden. Keine Rohtexte, keine Transkripte und keine Detailrubrics im Employer-Pfad.
                            </p>
                          )}
                        </div>

                        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                            <div>
                              <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Authority Filing / Immigration Preparation</p>
                              <p className="mt-2 text-slate-600">
                                Erstellt ein arbeitgeberfaehiges Behoerdenpaket mit Bundes-, Landes- und Stadtlogik. Tiefe Referenzpfade liegen aktuell fuer Berlin, Bayern/Muenchen, Nordrhein-Westfalen/Koeln, Baden-Wuerttemberg/Stuttgart, Hessen/Darmstadt-Frankfurt und Hamburg vor.
                              </p>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <Button
                                variant="outline"
                                className="dashboard-inline-button"
                                onClick={() => exportAuthorityFilingKit(row.id, "json")}
                                disabled={detailState.exporting}
                              >
                                JSON Export
                              </Button>
                              <Button
                                variant="outline"
                                className="dashboard-inline-button"
                                onClick={() => exportAuthorityFilingKit(row.id, "html")}
                                disabled={detailState.exporting}
                              >
                                HTML Export
                              </Button>
                            </div>
                          </div>

                          <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-5">
                            <div>
                              <label className="mb-2 flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-slate-400">
                                <span>Bundesland</span>
                                <FilingInfoHint hintKey="state" />
                              </label>
                              <Select
                                value={detailState.filingForm.federal_state || "__none__"}
                                onValueChange={(value) => handleFilingFormChange(row.id, "federal_state", value === "__none__" ? "" : value)}
                              >
                                <SelectTrigger className="dashboard-field">
                                  <SelectValue placeholder="Bundesland waehlen" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="__none__">Noch nicht festgelegt</SelectItem>
                                  {FEDERAL_STATE_OPTIONS.map((option) => (
                                    <SelectItem key={option} value={option}>
                                      {option}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            <div>
                              <label className="mb-2 flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-slate-400">
                                <span>Zielort / Stadt</span>
                                <FilingInfoHint hintKey="state" />
                              </label>
                              <Input
                                value={detailState.filingForm.city || ""}
                                onChange={(event) => handleFilingFormChange(row.id, "city", event.target.value)}
                                placeholder="z. B. Muenchen"
                                className="dashboard-field"
                              />
                            </div>

                            <div>
                              <label className="mb-2 flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-slate-400">
                                <span>Anerkennungsstatus</span>
                                <FilingInfoHint hintKey="recognition" />
                              </label>
                              <Select
                                value={detailState.filingForm.recognition_status}
                                onValueChange={(value) => handleFilingFormChange(row.id, "recognition_status", value)}
                              >
                                <SelectTrigger className="dashboard-field">
                                  <SelectValue placeholder="Anerkennungsstatus" />
                                </SelectTrigger>
                                <SelectContent>
                                  {RECOGNITION_STATUS_OPTIONS.map((option) => (
                                    <SelectItem key={option.value} value={option.value}>
                                      {option.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            <div>
                              <label className="mb-2 flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-slate-400">
                                <span>Reglementierter Beruf</span>
                                <FilingInfoHint hintKey="regulated" />
                              </label>
                              <Select
                                value={detailState.filingForm.regulated_profession}
                                onValueChange={(value) => handleFilingFormChange(row.id, "regulated_profession", value)}
                              >
                                <SelectTrigger className="dashboard-field">
                                  <SelectValue placeholder="Automatik oder Override" />
                                </SelectTrigger>
                                <SelectContent>
                                  {REGULATED_PROFESSION_OPTIONS.map((option) => (
                                    <SelectItem key={option.value} value={option.value}>
                                      {option.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            <div>
                              <label className="mb-2 flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-slate-400">
                                <span>Verfahrensart</span>
                                <FilingInfoHint hintKey="procedure" />
                              </label>
                              <Select
                                value={detailState.filingForm.fast_track}
                                onValueChange={(value) => handleFilingFormChange(row.id, "fast_track", value)}
                              >
                                <SelectTrigger className="dashboard-field">
                                  <SelectValue placeholder="Standard oder beschleunigt" />
                                </SelectTrigger>
                                <SelectContent>
                                  {PROCEDURE_OPTIONS.map((option) => (
                                    <SelectItem key={option.value} value={option.value}>
                                      {option.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>

                          <div className="mt-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                            <p className="text-xs text-slate-400">
                              Das Kit bereitet vor, fuellt aber nicht rechtsverbindlich ein und reicht nicht automatisch bei Behoerden ein.
                            </p>
                            <div className="flex flex-wrap gap-2">
                              <Button
                                variant="outline"
                                className="dashboard-inline-button"
                                onClick={() => refreshAuthorityDocuments(row.id)}
                                disabled={detailState.refreshingDocuments}
                              >
                                <RefreshCw className={`mr-2 h-4 w-4 ${detailState.refreshingDocuments ? "animate-spin" : ""}`} />
                                {detailState.refreshingDocuments ? "Bibliothek aktualisiert..." : "Offizielle Dokumente aktualisieren"}
                              </Button>
                              <Button
                                className="dashboard-action-button"
                                onClick={() => loadAuthorityFilingKit(row.id)}
                                disabled={detailState.filingLoading}
                              >
                                {detailState.filingLoading ? "Kit wird erstellt..." : "Authority Filing Kit erstellen"}
                              </Button>
                            </div>
                          </div>

                          {detailState.filingError ? (
                            <p className="mt-3 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
                              {detailState.filingError}
                            </p>
                          ) : null}
                          {detailState.filingFlash ? <p className="mt-3 text-sm text-emerald-700">{detailState.filingFlash}</p> : null}

                          {filingData ? (
                            <>
                              <StbAuthorityFilingEmployerWorkspace
                                row={row}
                                filingData={filingData}
                                detailState={detailState}
                                requestedRequestId={requestedRequestId}
                                activeTab={getActiveFilingTab(row.id)}
                                onTabChange={(nextTab) => setActiveFilingTab(row.id, nextTab)}
                                onExportKit={exportAuthorityFilingKit}
                                onFinalizeSubmissionPack={finalizeSubmissionPack}
                                onDownloadPreparedAsset={downloadPreparedAsset}
                                onDownloadAuthorityDocument={downloadAuthorityDocument}
                                onUpdateAuthorityTask={updateAuthorityTask}
                                onCreateCandidateRequest={createCandidateRequest}
                                onUpdateCandidateRequest={updateCandidateRequest}
                                onTaskNoteChange={handleTaskNoteChange}
                                getDeadlineTone={getDeadlineTone}
                                getFreshnessMeta={getFreshnessMeta}
                                getResponsibilityMeta={getResponsibilityMeta}
                                getReviewBucketMeta={getReviewBucketMeta}
                                formatCoverageLevel={formatCoverageLevel}
                                formatDateTime={formatDateTime}
                                formatDelta={formatDelta}
                                formatHistoryAction={formatHistoryAction}
                                formatRequestStatus={formatRequestStatus}
                                formatResponseStatus={formatResponseStatus}
                                formatTaskStatus={formatTaskStatus}
                                formatUrgencyLabel={formatUrgencyLabel}
                              />
                              {false ? (
                            <div className="mt-4 space-y-4">
                              <div className="rounded-xl border border-emerald-200 bg-gradient-to-r from-emerald-50 via-white to-sky-50 p-4">
                                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                                  <div>
                                    <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Employer Ready Filing Pack</p>
                                    <p className="mt-2 text-xl font-semibold text-slate-900">{filingData.employer_ready_pack?.cover_title || filingData.filing_packet_summary?.headline}</p>
                                    <p className="mt-2 text-slate-600">
                                      {filingData.classification?.procedure_label} · {filingData.classification?.federal_state}
                                      {filingData.classification?.city ? ` / ${filingData.classification.city}` : ""}
                                      {" · "}
                                      {filingData.classification?.regulated_profession ? "reglementierter Beruf" : "nicht reglementierter Beruf"}
                                    </p>
                                    <p className="mt-2 text-sm text-slate-600">
                                      {filingData.review_workflow?.overall_label || "Pack-Reifegrad wird berechnet"} ·{" "}
                                      {filingData.document_library_status?.current_locally_checked_documents || 0} lokal gepruefte offizielle Dokumente
                                    </p>
                                  </div>
                                  <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
                                    <div className="rounded-lg border border-white/70 bg-white/80 p-3">
                                      <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Ready to sign</p>
                                      <p className="mt-2 text-2xl font-semibold text-slate-900">{filingData.review_workflow?.counts?.ready_to_sign || 0}</p>
                                    </div>
                                    <div className="rounded-lg border border-white/70 bg-white/80 p-3">
                                      <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Ready to upload</p>
                                      <p className="mt-2 text-2xl font-semibold text-slate-900">{filingData.review_workflow?.counts?.ready_to_upload || 0}</p>
                                    </div>
                                    <div className="rounded-lg border border-white/70 bg-white/80 p-3">
                                      <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Vorbefuellte Originale</p>
                                      <p className="mt-2 text-2xl font-semibold text-slate-900">{filingData.fillability_summary?.fillable_pdf || 0}</p>
                                    </div>
                                    <div className="rounded-lg border border-white/70 bg-white/80 p-3">
                                      <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Transferhilfen</p>
                                      <p className="mt-2 text-2xl font-semibold text-slate-900">{filingData.fillability_summary?.structured_transfer_only || 0}</p>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                                <div className="rounded-lg border border-slate-200 bg-white p-3">
                                  <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Verfahren</p>
                                  <p className="mt-2 text-slate-900">{filingData.classification?.procedure_explainer?.label || filingData.classification?.pathway_title}</p>
                                  <p className="mt-1 text-slate-600">{filingData.classification?.procedure_explainer?.why_it_fits || filingData.classification?.visa_path}</p>
                                </div>
                                <div className="rounded-lg border border-slate-200 bg-white p-3">
                                  <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Ort</p>
                                  <p className="mt-2 text-slate-900">{filingData.classification?.federal_state}</p>
                                  <p className="mt-1 text-slate-600">{filingData.classification?.city || "noch offen"}</p>
                                </div>
                                <div className="rounded-lg border border-slate-200 bg-white p-3">
                                  <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Reglementierung</p>
                                  <p className="mt-2 text-slate-900">{filingData.classification?.regulation_explainer?.label || (filingData.classification?.regulated_profession ? "Reglementiert" : "Nicht reglementiert")}</p>
                                  <p className="mt-1 text-slate-600">Anerkennung: {filingData.classification?.recognition_status_label || filingData.classification?.recognition_status}</p>
                                </div>
                                <div className="rounded-lg border border-slate-200 bg-white p-3">
                                  <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Bibliothek</p>
                                  <p className="mt-2 text-slate-900">Lokal vorhanden: {filingData.document_library_status?.locally_available_documents || 0}</p>
                                  <p className="mt-1 text-slate-600">Refresh-faehig: {filingData.document_library_status?.refreshable_documents || 0}</p>
                                  <p className="mt-1 text-slate-600">Fillable: {filingData.document_library_status?.fillable_pdf_documents || 0} · Transfer: {filingData.document_library_status?.structured_transfer_documents || 0}</p>
                                  <p className="mt-1 text-slate-600">Geprueft: {filingData.document_library_status?.current_locally_checked_documents || 0} · unklar: {filingData.document_library_status?.local_unknown_currency_documents || 0}</p>
                                </div>
                              </div>

                              <div className="grid gap-4 xl:grid-cols-2">
                                <div className="rounded-lg border border-slate-200 bg-white p-4">
                                  <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Verfahrensfolgen</p>
                                  <p className="mt-3 text-sm text-slate-700">{filingData.procedure_difference?.why_it_fits}</p>
                                  <ul className="mt-3 list-disc pl-5 text-sm text-slate-600">
                                    {(filingData.procedure_difference?.practical_effects || []).map((entry) => (
                                      <li key={entry}>{entry}</li>
                                    ))}
                                  </ul>
                                </div>

                                <div className="rounded-lg border border-slate-200 bg-white p-4">
                                  <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Reglementierung</p>
                                  <p className="mt-3 text-sm text-slate-700">{filingData.regulation_difference?.why_it_fits}</p>
                                  <ul className="mt-3 list-disc pl-5 text-sm text-slate-600">
                                    {(filingData.regulation_difference?.practical_effects || []).map((entry) => (
                                      <li key={entry}>{entry}</li>
                                    ))}
                                  </ul>
                                </div>
                              </div>

                              <div className="rounded-lg border border-emerald-200 bg-emerald-50/70 p-4">
                                <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Filing Packet Summary</p>
                                {filingData.filing_packet_summary?.summary?.map((line) => (
                                  <p key={line} className="mt-2 text-slate-700">{line}</p>
                                ))}
                              </div>

                              <div className="grid gap-4 xl:grid-cols-[1.35fr,0.95fr]">
                                <div className="rounded-lg border border-slate-200 bg-white p-4">
                                  <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                                    <div>
                                      <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Employer Task Board</p>
                                      <p className="mt-2 text-lg font-semibold text-slate-900">
                                        {filingData.task_board?.open_count || 0} offene Aufgaben · {filingData.task_board?.completed_count || 0} erledigt
                                      </p>
                                      <p className="mt-1 text-sm text-slate-600">
                                        {filingData.submission_pack_state?.current_stage_label || "Working Pack"} · {filingData.submission_pack_state?.stage_reason}
                                      </p>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                      <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-600">
                                        Signierbar {filingData.task_board?.signable_count || 0}
                                      </span>
                                      <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-600">
                                        Uploadbar {filingData.task_board?.uploadable_count || 0}
                                      </span>
                                      <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-600">
                                        Blockiert {filingData.task_board?.blocked_count || 0}
                                      </span>
                                    </div>
                                  </div>
                                  {filingData.task_board?.summary_lines?.length ? (
                                    <ul className="mt-3 list-disc pl-5 text-sm text-slate-700">
                                      {filingData.task_board.summary_lines.map((line) => (
                                        <li key={line}>{line}</li>
                                      ))}
                                    </ul>
                                  ) : null}
                                  <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                                    {(filingData.task_board?.lanes || []).map((lane) => (
                                      <div key={`lane-${lane.owner_role}`} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                        <div className="flex items-center justify-between gap-2">
                                          <p className="font-semibold text-slate-900">{lane.label}</p>
                                          <span className={`rounded-full border px-2 py-1 text-[11px] ${getResponsibilityMeta(lane.owner_role).tone}`}>
                                            {lane.open_count} offen
                                          </span>
                                        </div>
                                        <p className="mt-2 text-xs text-slate-500">
                                          Ready now {lane.ready_now_count || 0} · Blocker {lane.blocking_count || 0}
                                        </p>
                                        <ul className="mt-2 list-disc pl-5 text-xs text-slate-600">
                                          {(lane.tasks || []).slice(0, 4).map((task) => (
                                            <li key={`lane-task-${task.task_id}`}>{task.title}</li>
                                          ))}
                                        </ul>
                                      </div>
                                    ))}
                                  </div>
                                </div>

                                <div className="rounded-lg border border-slate-200 bg-white p-4">
                                  <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Final Submission Pack</p>
                                  <p className="mt-2 text-lg font-semibold text-slate-900">
                                    {filingData.submission_pack_state?.current_stage_label || "Draft Pack"}
                                  </p>
                                  <p className="mt-1 text-sm text-slate-600">{filingData.submission_pack_state?.stage_reason}</p>
                                  <div className="mt-3 grid gap-2 sm:grid-cols-3">
                                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                      <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Open</p>
                                      <p className="mt-2 font-semibold text-slate-900">{filingData.submission_pack_state?.open_task_count || 0}</p>
                                    </div>
                                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                      <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Requests</p>
                                      <p className="mt-2 font-semibold text-slate-900">{filingData.submission_pack_state?.active_request_count || 0}</p>
                                    </div>
                                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                      <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Final Packs</p>
                                      <p className="mt-2 font-semibold text-slate-900">{filingData.final_submission_packs?.length || 0}</p>
                                    </div>
                                  </div>
                                  <div className="mt-3 grid gap-2 sm:grid-cols-3">
                                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                                      <p className="text-xs uppercase tracking-[0.14em] text-amber-700">Due soon</p>
                                      <p className="mt-2 font-semibold text-slate-900">{filingData.submission_pack_state?.due_soon_request_count || 0}</p>
                                    </div>
                                    <div className="rounded-lg border border-rose-200 bg-rose-50 p-3">
                                      <p className="text-xs uppercase tracking-[0.14em] text-rose-700">Overdue</p>
                                      <p className="mt-2 font-semibold text-slate-900">{filingData.submission_pack_state?.overdue_request_count || 0}</p>
                                    </div>
                                    <div className="rounded-lg border border-sky-200 bg-sky-50 p-3">
                                      <p className="text-xs uppercase tracking-[0.14em] text-sky-700">Notifications</p>
                                      <p className="mt-2 font-semibold text-slate-900">{filingData.submission_pack_state?.notification_count || 0}</p>
                                    </div>
                                  </div>
                                  <div className="mt-4 flex flex-wrap gap-2">
                                    <Button
                                      className="dashboard-action-button"
                                      onClick={() => finalizeSubmissionPack(row.id)}
                                      disabled={!filingData.submission_pack_state?.can_finalize || detailState.finalizingPack}
                                    >
                                      {detailState.finalizingPack ? "Pack wird finalisiert..." : "Pack finalisieren"}
                                    </Button>
                                    {!filingData.submission_pack_state?.can_finalize ? (
                                      <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                                        Finalisierung erst ohne blockierende Tasks und kritische ueberfaellige Requests sinnvoll.
                                      </span>
                                    ) : null}
                                  </div>
                                  {filingData.final_submission_packs?.length ? (
                                    <div className="mt-4 space-y-3">
                                      {filingData.final_submission_packs.slice(0, 3).map((pack) => (
                                        <div key={`final-pack-${pack.final_pack_id}`} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                          <div className="flex flex-wrap items-center justify-between gap-2">
                                            <p className="font-semibold text-slate-900">Pack {pack.pack_no} · {pack.stage_label || pack.stage}</p>
                                            <span className="text-xs text-slate-500">{pack.created_at}</span>
                                          </div>
                                          <div className="mt-2 flex flex-wrap gap-2">
                                            {(pack.pack_assets || []).map((asset) => (
                                              <Button
                                                key={`final-pack-asset-${asset.asset_id}`}
                                                variant="outline"
                                                className="dashboard-inline-button"
                                                onClick={() => downloadPreparedAsset(row.id, asset)}
                                                disabled={detailState.exporting}
                                              >
                                                {asset.file_name}
                                              </Button>
                                            ))}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  ) : null}
                                </div>
                              </div>

                              <div className="rounded-lg border border-slate-200 bg-white p-4">
                                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                                  <div>
                                    <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Version Compare</p>
                                    <p className="mt-2 text-lg font-semibold text-slate-900">
                                      {filingData.version_compare?.summary_lines?.length
                                        ? "Aenderungen seit der letzten Version"
                                        : "Noch keine Vorversion fuer den Vergleich"}
                                    </p>
                                    <p className="mt-1 text-sm text-slate-600">
                                      Sichtbar werden Dokument-, Missing-Doc-, Reife-, Behoerden- und Bibliotheksaenderungen, ohne rohe JSON-Diffs offenzulegen.
                                    </p>
                                  </div>
                                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
                                    Compare: <span className="font-semibold text-slate-900">{filingData.version_compare ? "letzte Version vs. aktuell" : "erste Version"}</span>
                                  </div>
                                </div>
                                {filingData.version_compare?.summary_lines?.length ? (
                                  <>
                                    <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3">
                                      <ul className="list-disc pl-5 text-sm text-slate-700">
                                        {filingData.version_compare.summary_lines.map((line) => (
                                          <li key={line}>{line}</li>
                                        ))}
                                      </ul>
                                    </div>
                                    <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                        <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Dokumente</p>
                                        <p className="mt-2 text-sm text-slate-900">Neu {formatDelta(filingData.version_compare.document_changes?.added?.length || 0)} · Entfallen {formatDelta(-1 * (filingData.version_compare.document_changes?.removed?.length || 0))}</p>
                                        <p className="mt-1 text-xs text-slate-500">Geaendert: {filingData.version_compare.changed_documents?.length || 0}</p>
                                      </div>
                                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                        <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Missing Docs</p>
                                        <p className="mt-2 text-sm text-slate-900">Delta {formatDelta(filingData.version_compare.missing_docs_delta)}</p>
                                        <p className="mt-1 text-xs text-slate-500">Geschlossen {(filingData.version_compare.missing_document_changes?.removed || []).length} · Neu {(filingData.version_compare.missing_document_changes?.added || []).length}</p>
                                      </div>
                                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                        <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Review / Completion</p>
                                        <p className="mt-2 text-sm text-slate-900">Ready to sign {formatDelta(filingData.version_compare.ready_to_sign_delta)} · Ready to upload {formatDelta(filingData.version_compare.ready_to_upload_delta)}</p>
                                        <p className="mt-1 text-xs text-slate-500">Completion {formatDelta(filingData.version_compare.completion_progress_delta)}% · Tasks {formatDelta(filingData.version_compare.open_tasks_delta || 0)} · Fresh responses {formatDelta(filingData.version_compare.fresh_response_delta || 0)}</p>
                                      </div>
                                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                        <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Bibliothek</p>
                                        <p className="mt-2 text-sm text-slate-900">Geprueft {formatDelta(filingData.version_compare.library_checked_delta)} · unklar {formatDelta(filingData.version_compare.library_uncertain_delta)}</p>
                                        <p className="mt-1 text-xs text-slate-500">Nur beschrieben {formatDelta(filingData.version_compare.described_only_delta)} · Requests {formatDelta(filingData.version_compare.active_requests_delta || 0)} · Due soon {formatDelta(filingData.version_compare.due_soon_requests_delta || 0)} · Overdue {formatDelta(filingData.version_compare.overdue_requests_delta || 0)}</p>
                                      </div>
                                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                        <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Operative Signale</p>
                                        <p className="mt-2 text-sm text-slate-900">Notifications {formatDelta(filingData.version_compare.notification_delta || 0)} · Final Packs {formatDelta(filingData.version_compare.final_pack_delta || 0)}</p>
                                        <p className="mt-1 text-xs text-slate-500">Candidate silent {formatDelta(filingData.version_compare.stale_candidate_requests_delta || 0)} · Review backlog {formatDelta(filingData.version_compare.stale_employer_review_delta || 0)}</p>
                                      </div>
                                    </div>
                                    <div className="mt-4 grid gap-4 xl:grid-cols-3">
                                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                        <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Dokumentaenderungen</p>
                                        <ul className="mt-2 list-disc pl-5 text-sm text-slate-700">
                                          {(filingData.version_compare.changed_documents || []).slice(0, 4).map((entry) => (
                                            <li key={`compare-doc-${entry.document_id}`}>
                                              {entry.title}: {entry.deltas.map((delta) => `${delta.field} ${delta.previous || "n/a"} -> ${delta.current || "n/a"}`).join(" · ")}
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                        <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Behoerdenauflosung</p>
                                        <ul className="mt-2 list-disc pl-5 text-sm text-slate-700">
                                          {(filingData.version_compare.authority_resolution_changes || []).slice(0, 4).map((entry) => (
                                            <li key={`compare-authority-${entry.authority_id}`}>
                                              {entry.title}: {entry.previous_state || "n/a"} / {entry.previous_city || "n/a"} -> {entry.current_state || "n/a"} / {entry.current_city || "n/a"}
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                        <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Pack-Reife</p>
                                        <p className="mt-2 text-sm text-slate-700">
                                          {filingData.version_compare.current_pack_label
                                            ? `${filingData.version_compare.previous_pack_label || "n/a"} -> ${filingData.version_compare.current_pack_label}`
                                            : "Noch keine Reifeaenderung hinterlegt."}
                                        </p>
                                        <p className="mt-2 text-xs text-slate-500">
                                          Naechster Schritt {filingData.version_compare.ready_for_next_step_changed ? "hat sich geaendert" : "unveraendert"}.
                                        </p>
                                      </div>
                                    </div>
                                  </>
                                ) : (
                                  <p className="mt-4 text-sm text-slate-600">
                                    Sobald mindestens ein frueherer Kit-Stand existiert, zeigt diese Ansicht direkt die arbeitsrelevanten Unterschiede zum aktuellen Paket.
                                  </p>
                                )}
                              </div>

                              <div className="rounded-lg border border-slate-200 bg-white p-4">
                                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                                  <div>
                                    <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Review- und Submission-Workflow</p>
                                    <p className="mt-2 text-lg font-semibold text-slate-900">{filingData.review_workflow?.overall_label}</p>
                                    <p className="mt-1 text-sm text-slate-600">
                                      {filingData.review_workflow?.case_ready_for_next_authority_step
                                        ? "Der Fall ist fuer den naechsten behoerdlichen Upload-/Weitergabeschritt vorbereitet."
                                        : "Vor der naechsten Behoerdenstufe sind noch Review-, Signatur- oder Missing-Doc-Schritte offen."}
                                    </p>
                                  </div>
                                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
                                    Stage: <span className="font-semibold text-slate-900">{filingData.review_workflow?.overall_stage}</span>
                                  </div>
                                </div>
                                <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-5">
                                  {Object.entries(filingData.review_workflow?.buckets || {}).map(([bucketKey, bucketEntries]) => {
                                    const meta = getReviewBucketMeta(bucketKey);
                                    return (
                                      <div key={bucketKey} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                        <span className={`inline-flex rounded-full border px-2 py-1 text-xs font-medium ${meta.tone}`}>{meta.label}</span>
                                        <p className="mt-3 text-2xl font-semibold text-slate-900">{bucketEntries.length}</p>
                                        <ul className="mt-2 list-disc pl-5 text-sm text-slate-600">
                                          {bucketEntries.slice(0, 3).map((entry) => (
                                            <li key={`${bucketKey}-${entry.document_id}`}>{entry.title}</li>
                                          ))}
                                        </ul>
                                      </div>
                                    );
                                  })}
                                </div>
                                {filingData.review_workflow?.next_actions?.length ? (
                                  <div className="mt-4 rounded-lg border border-sky-200 bg-sky-50 p-3">
                                    <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Naechste Schritte</p>
                                    <ul className="mt-2 list-disc pl-5 text-sm text-slate-700">
                                      {filingData.review_workflow.next_actions.map((entry) => (
                                        <li key={entry}>{entry}</li>
                                      ))}
                                    </ul>
                                  </div>
                                ) : null}
                              </div>

                              <div className="rounded-lg border border-slate-200 bg-white p-4">
                                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                                  <div>
                                    <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Completion Workflow</p>
                                    <p className="mt-2 text-lg font-semibold text-slate-900">
                                      {filingData.completion_workflow?.ready_for_next_step
                                        ? "Naechster Behoerdenschritt ist vorbereitet"
                                        : "Es gibt noch aktive Blocker vor dem naechsten Behoerdenschritt"}
                                    </p>
                                    <p className="mt-1 text-sm text-slate-600">
                                      Missing Docs und manuelle Schritte werden hier in eine konkrete Abarbeitung fuer Arbeitgeber, Bewerber und externe Stellen uebersetzt.
                                    </p>
                                  </div>
                                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
                                    Fortschritt: <span className="font-semibold text-slate-900">{filingData.completion_workflow?.progress_percent || 0}%</span>
                                  </div>
                                </div>
                                <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-5">
                                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                                    <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Jetzt signierbar</p>
                                    <p className="mt-2 text-2xl font-semibold text-slate-900">{filingData.completion_workflow?.sign_now_count || 0}</p>
                                  </div>
                                  <div className="rounded-lg border border-sky-200 bg-sky-50 p-3">
                                    <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Jetzt uploadbereit</p>
                                    <p className="mt-2 text-2xl font-semibold text-slate-900">{filingData.completion_workflow?.done_now_count || 0}</p>
                                  </div>
                                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                                    <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Blocker</p>
                                    <p className="mt-2 text-2xl font-semibold text-slate-900">{filingData.completion_workflow?.blocking_count || 0}</p>
                                  </div>
                                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                    <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Bearbeiterrollen</p>
                                    <p className="mt-2 text-2xl font-semibold text-slate-900">{filingData.completion_workflow?.responsibility_summary?.length || 0}</p>
                                  </div>
                                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                    <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Abschlussstatus</p>
                                    <p className="mt-2 text-sm font-semibold text-slate-900">
                                      {filingData.completion_workflow?.ready_for_next_step ? "bereit fuer naechsten Schritt" : "noch nicht abgeschlossen"}
                                    </p>
                                  </div>
                                </div>
                                {filingData.completion_workflow?.summary_lines?.length ? (
                                  <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3">
                                    <ul className="list-disc pl-5 text-sm text-slate-700">
                                      {filingData.completion_workflow.summary_lines.map((line) => (
                                        <li key={line}>{line}</li>
                                      ))}
                                    </ul>
                                  </div>
                                ) : null}
                                <div className="mt-4 grid gap-4 xl:grid-cols-[0.95fr,1.05fr]">
                                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                    <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Jetzt erledigbare Schritte</p>
                                    <div className="mt-3 space-y-3">
                                      {filingData.completion_workflow?.immediate_actions?.length ? filingData.completion_workflow.immediate_actions.map((entry) => (
                                        <div key={`completion-immediate-${entry.document_id}`} className="rounded-lg border border-white bg-white p-3">
                                          <div className="flex flex-wrap items-center gap-2">
                                            <p className="font-semibold text-slate-900">{entry.title}</p>
                                            <span className={`rounded-full border px-2 py-1 text-xs ${getReviewBucketMeta(entry.review_bucket).tone}`}>
                                              {entry.status_label || getReviewBucketMeta(entry.review_bucket).label}
                                            </span>
                                          </div>
                                          <p className="mt-1 text-sm text-slate-600">{entry.action_label}</p>
                                          <p className="mt-1 text-xs text-slate-500">
                                            Verantwortlich: {entry.owner_label}
                                            {entry.signature_required ? " · Unterschrift erforderlich" : ""}
                                          </p>
                                        </div>
                                      )) : (
                                        <p className="text-sm text-slate-600">Aktuell keine sofort erledigbaren Schritte sichtbar.</p>
                                      )}
                                    </div>
                                  </div>
                                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                    <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Arbeitslanes nach Verantwortlichen</p>
                                    <div className="mt-3 space-y-3">
                                      {filingData.completion_workflow?.task_lanes?.length ? filingData.completion_workflow.task_lanes.map((lane) => (
                                        <div key={`lane-${lane.key}`} className="rounded-lg border border-white bg-white p-3">
                                          <div className="flex flex-wrap items-center justify-between gap-2">
                                            <div className="flex items-center gap-2">
                                              <span className={`rounded-full border px-2 py-1 text-xs ${getResponsibilityMeta(lane.key).tone}`}>{lane.label}</span>
                                              <p className="text-sm text-slate-600">offen {lane.open_count} · blocker {lane.blocking_count} · ready now {lane.ready_now_count}</p>
                                            </div>
                                            <span className="rounded-full border border-slate-200 px-2 py-1 text-xs text-slate-500">{lane.total_count}</span>
                                          </div>
                                          <ul className="mt-2 list-disc pl-5 text-sm text-slate-700">
                                            {lane.tasks.slice(0, 3).map((task) => (
                                              <li key={`lane-${lane.key}-${task.document_id}`}>{task.title} - {task.action_label}</li>
                                            ))}
                                          </ul>
                                        </div>
                                      )) : (
                                        <p className="text-sm text-slate-600">Noch keine Verantwortlichkeits-Lanes berechnet.</p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <div className="mt-4 grid gap-4 xl:grid-cols-[1.1fr,0.9fr]">
                                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                    <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Blockierende Aufgaben</p>
                                    <div className="mt-3 space-y-3">
                                      {filingData.completion_workflow?.next_blockers?.length ? filingData.completion_workflow.next_blockers.map((entry) => (
                                        <div key={`completion-${entry.document_id}`} className="rounded-lg border border-white bg-white p-3">
                                          <div className="flex flex-wrap items-center gap-2">
                                            <p className="font-semibold text-slate-900">{entry.title}</p>
                                            <span className={`rounded-full border px-2 py-1 text-xs ${getReviewBucketMeta(entry.review_bucket).tone}`}>
                                              {getReviewBucketMeta(entry.review_bucket).label}
                                            </span>
                                          </div>
                                          <p className="mt-1 text-sm text-slate-600">{entry.action_label}</p>
                                          <p className="mt-1 text-xs text-slate-500">
                                            Verantwortlich: {entry.responsibility?.label}
                                            {entry.signature_required ? " · Unterschrift erforderlich" : ""}
                                          </p>
                                          {entry.notes?.length ? (
                                            <ul className="mt-2 list-disc pl-5 text-xs text-slate-600">
                                              {entry.notes.slice(0, 3).map((note) => (
                                                <li key={`${entry.document_id}-${note}`}>{note}</li>
                                              ))}
                                            </ul>
                                          ) : null}
                                        </div>
                                      )) : (
                                        <p className="text-sm text-slate-600">Keine blockierenden Aufgaben mehr.</p>
                                      )}
                                    </div>
                                  </div>
                                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                    <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Verantwortlichkeiten</p>
                                    <div className="mt-3 space-y-3">
                                      {filingData.completion_workflow?.responsibility_summary?.length ? filingData.completion_workflow.responsibility_summary.map((entry) => (
                                        <div key={entry.label} className="rounded-lg border border-white bg-white p-3">
                                          <div className="flex items-center justify-between gap-2">
                                            <div className="flex items-center gap-2">
                                              <span className={`rounded-full border px-2 py-1 text-xs ${getResponsibilityMeta(entry.key).tone}`}>{entry.label}</span>
                                              <p className="text-xs text-slate-500">Blocker {entry.blocking_count || 0} · ready now {entry.ready_now_count || 0}</p>
                                            </div>
                                            <span className="rounded-full border border-slate-200 px-2 py-1 text-xs text-slate-500">{entry.count}</span>
                                          </div>
                                        </div>
                                      )) : (
                                        <p className="text-sm text-slate-600">Noch keine Completion-Verantwortlichkeiten berechnet.</p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div className="grid gap-4 xl:grid-cols-[1.2fr,0.8fr]">
                                <div className="rounded-lg border border-slate-200 bg-white p-4">
                                  <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Einreichungsreihenfolge</p>
                                  <div className="mt-3 space-y-3">
                                    {(filingData.employer_ready_pack?.ordered_steps || []).map((step) => (
                                      <div key={step.step} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                        <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Schritt {step.step}</p>
                                        <p className="mt-1 font-semibold text-slate-900">{step.title}</p>
                                        <p className="mt-1 text-sm text-slate-600">{step.description}</p>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                                <div className="rounded-lg border border-slate-200 bg-white p-4">
                                  <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Behoerdenkontaktblock</p>
                                  <div className="mt-3 space-y-3">
                                    {(filingData.employer_ready_pack?.authority_contact_block || []).map((entry) => (
                                      <div key={`${entry.title}-${entry.city || entry.state}`} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                        <p className="font-semibold text-slate-900">{entry.title}</p>
                                        <p className="mt-1 text-sm text-slate-600">{entry.state}{entry.city ? ` / ${entry.city}` : ""}</p>
                                        <a href={entry.source_url} target="_blank" rel="noreferrer" className="mt-2 inline-block text-xs text-sky-700">
                                          Offizielle Quelle
                                        </a>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>

                              <div className="rounded-lg border border-slate-200 bg-white p-4">
                                <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Foederale Aufloesung</p>
                                <p className="mt-3 text-sm text-slate-700">{filingData.jurisdiction_resolution?.summary}</p>
                                <div className="mt-3 grid gap-3 xl:grid-cols-3">
                                  {(filingData.jurisdiction_resolution?.layers || []).map((layer) => (
                                    <div key={layer.level} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                      <p className="text-xs uppercase tracking-[0.14em] text-slate-400">{layer.level}</p>
                                      <p className="mt-2 font-semibold text-slate-900">{layer.title}</p>
                                      <p className="mt-1 text-sm text-slate-600">{layer.effect}</p>
                                    </div>
                                  ))}
                                </div>
                              </div>

                              {filingData.document_refresh ? (
                                <div className="rounded-lg border border-emerald-200 bg-emerald-50/70 p-4">
                                  <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Letzte Bibliotheksaktualisierung</p>
                                  <p className="mt-2 text-slate-700">
                                    {filingData.document_refresh.updated_count} Dokumente aktualisiert, {filingData.document_refresh.skipped_count} uebersprungen, {filingData.document_refresh.error_count} Fehler.
                                  </p>
                                </div>
                              ) : null}

                              <div className="rounded-lg border border-slate-200 bg-white p-4">
                                <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
                                  <div>
                                    <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Dokumentenfrische und Bibliotheksstand</p>
                                    <p className="mt-2 text-sm text-slate-600">
                                      Sichtbar ist, welche offiziellen Unterlagen lokal geprueft, nur beschrieben oder aktuell unklar sind.
                                    </p>
                                  </div>
                                  <div className="text-sm text-slate-500">
                                    Lokal geprueft: {filingData.document_library_status?.current_locally_checked_documents || 0}
                                    <div>Letzte Pruefung: {filingData.document_library_status?.last_checked_at || "n/a"}</div>
                                    <div>Letzter Download: {filingData.document_library_status?.last_downloaded_at || "n/a"}</div>
                                  </div>
                                </div>
                                <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                    <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Aktuell lokal geprueft</p>
                                    <p className="mt-2 text-2xl font-semibold text-slate-900">{filingData.document_library_status?.current_locally_checked_documents || 0}</p>
                                  </div>
                                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                    <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Aktualitaet unklar</p>
                                    <p className="mt-2 text-2xl font-semibold text-slate-900">{filingData.document_library_status?.local_unknown_currency_documents || 0}</p>
                                  </div>
                                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                    <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Nur offiziell beschrieben</p>
                                    <p className="mt-2 text-2xl font-semibold text-slate-900">{filingData.document_library_status?.officially_described_documents || 0}</p>
                                  </div>
                                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                    <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Refresh-faehig</p>
                                    <p className="mt-2 text-2xl font-semibold text-slate-900">{filingData.document_library_status?.refreshable_documents || 0}</p>
                                  </div>
                                </div>
                                {(filingData.document_library_status?.uncertain_titles?.length || filingData.document_library_status?.described_only_titles?.length) ? (
                                  <div className="mt-4 grid gap-3 xl:grid-cols-2">
                                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                                      <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Aktualitaet unklar</p>
                                      <ul className="mt-2 list-disc pl-5 text-sm text-slate-700">
                                        {(filingData.document_library_status?.uncertain_titles || []).slice(0, 6).map((title) => (
                                          <li key={`uncertain-${title}`}>{title}</li>
                                        ))}
                                      </ul>
                                    </div>
                                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                      <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Nur offiziell beschrieben</p>
                                      <ul className="mt-2 list-disc pl-5 text-sm text-slate-700">
                                        {(filingData.document_library_status?.described_only_titles || []).slice(0, 6).map((title) => (
                                          <li key={`described-${title}`}>{title}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  </div>
                                ) : null}
                              </div>

                              <div className="grid gap-4 xl:grid-cols-2">
                                <div className="rounded-lg border border-slate-200 bg-white p-4">
                                  <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Behoerdenliste</p>
                                  <div className="mt-3 space-y-3">
                                    {filingData.authority_matrix?.map((entry) => (
                                      <div key={`${entry.title}-${entry.level}`} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                        <p className="font-semibold text-slate-900">{entry.title}</p>
                                        <p className="text-sm text-slate-600">{entry.scope}</p>
                                        <p className="mt-1 text-xs text-slate-500">{entry.resolved_state || "Bund"} · {entry.resolved_city || "ohne Ortsbindung"}</p>
                                        <a href={entry.source_url} target="_blank" rel="noreferrer" className="mt-1 inline-block text-xs text-sky-700">
                                          Quelle oeffnen
                                        </a>
                                      </div>
                                    ))}
                                  </div>
                                </div>

                                <div className="rounded-lg border border-slate-200 bg-white p-4">
                                  <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Fehlende / manuelle Unterlagen</p>
                                  <div className="mt-3 space-y-3">
                                    {filingData.missing_documents?.length ? filingData.missing_documents.map((entry) => (
                                      <div key={entry.document_id} className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                                        <p className="font-semibold text-slate-900">{entry.title}</p>
                                        <p className="text-sm text-slate-600">{entry.status}</p>
                                        <p className="mt-1 text-xs text-slate-500">
                                          Verantwortlich: {entry.responsibility?.label || "manuell klaeren"}
                                          {entry.signature_required ? " · Unterschrift erforderlich" : ""}
                                          {entry.review_bucket ? ` · ${getReviewBucketMeta(entry.review_bucket).label}` : ""}
                                        </p>
                                        {entry.responsibility?.detail ? (
                                          <p className="mt-1 text-xs text-slate-500">{entry.responsibility.detail}</p>
                                        ) : null}
                                        {entry.notes?.length ? (
                                          <ul className="mt-2 list-disc pl-5 text-xs text-slate-600">
                                            {entry.notes.map((note) => (
                                              <li key={`${entry.document_id}-${note}`}>{note}</li>
                                            ))}
                                          </ul>
                                        ) : null}
                                      </div>
                                    )) : (
                                      <p className="text-sm text-slate-600">Keine zusaetzlichen Missing-Docs erkannt.</p>
                                    )}
                                  </div>
                                </div>
                              </div>

                              <div className="rounded-lg border border-slate-200 bg-white p-4">
                                <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Formular- und Dokumentenliste</p>
                                <div className="mt-3 grid gap-3 xl:grid-cols-2">
                                  {filingData.documents?.map((entry) => (
                                    <div key={entry.id} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                      <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
                                        <div>
                                          <p className="font-semibold text-slate-900">{entry.title}</p>
                                          <p className="text-sm text-slate-600">{entry.authority}</p>
                                          <p className="mt-1 text-xs text-slate-500">
                                            {entry.level} · {entry.checklist_status?.status} · {entry.usable_mode}
                                          </p>
                                          <p className="mt-1 text-xs text-slate-500">
                                            Letzter Download: {entry.downloaded_at || "noch nicht lokal"} · Letzte Pruefung: {entry.last_checked_at || "n/a"}
                                          </p>
                                          <p className="mt-1 text-xs text-slate-500">
                                            PDF-Felder: {entry.pdf_field_count ?? 0} · Auto: {entry.autofill_field_count ?? 0} · Manuell: {entry.manual_field_count ?? 0}
                                          </p>
                                          <p className="mt-1 text-xs text-slate-500">
                                            Abdeckung: {formatCoverageLevel(entry.field_coverage_level)} · Confidence: {entry.fillability_confidence ?? "n/a"}
                                            {entry.signature_required ? " · Unterschrift erforderlich" : ""}
                                          </p>
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                          {entry.download_url ? (
                                            <Button
                                              variant="outline"
                                              className="dashboard-inline-button"
                                              onClick={() => downloadAuthorityDocument(row.id, entry)}
                                              disabled={detailState.exporting}
                                            >
                                              Offizielles Dokument
                                            </Button>
                                          ) : null}
                                          <span className={`rounded-full border px-2 py-1 text-xs ${getReviewBucketMeta(entry.review_bucket).tone}`}>
                                            {getReviewBucketMeta(entry.review_bucket).label}
                                          </span>
                                          <span className="rounded-full border border-slate-200 px-2 py-1 text-xs text-slate-500">
                                            {entry.fillability_class === "fillable_pdf" ? "direkt vorbefuellbar" : entry.fillability_class === "structured_transfer_only" ? "Transferhilfe" : entry.fillability_class === "manual_only" ? "manuell" : "Referenz"}
                                          </span>
                                          {entry.freshness ? (
                                            <span className={`rounded-full border px-2 py-1 text-xs ${getFreshnessMeta(entry.freshness.freshness_status).tone}`}>
                                              {entry.freshness.label}
                                            </span>
                                          ) : null}
                                        </div>
                                      </div>
                                      {entry.fillability_reason ? (
                                        <p className="mt-2 text-sm text-slate-600">{entry.fillability_reason}</p>
                                      ) : null}
                                      {entry.review_note ? (
                                        <p className="mt-2 text-sm text-slate-600">{entry.review_note}</p>
                                      ) : null}
                                      {entry.plausibility_warnings?.length ? (
                                        <ul className="mt-2 list-disc pl-5 text-sm text-amber-700">
                                          {entry.plausibility_warnings.map((note) => <li key={note}>{note}</li>)}
                                        </ul>
                                      ) : null}
                                      {entry.checklist_status?.notes?.length ? (
                                        <ul className="mt-2 list-disc pl-5 text-sm text-slate-600">
                                          {entry.checklist_status.notes.map((note) => <li key={note}>{note}</li>)}
                                        </ul>
                                      ) : null}
                                      {entry.review_requirements?.length ? (
                                        <div className="mt-3 flex flex-wrap gap-2">
                                          {entry.review_requirements.map((requirement) => (
                                            <span key={`${entry.id}-${requirement}`} className="rounded-full border border-slate-200 bg-white px-2 py-1 text-xs text-slate-500">
                                              {requirement}
                                            </span>
                                          ))}
                                        </div>
                                      ) : null}
                                    </div>
                                  ))}
                                </div>
                              </div>

                              <div className="grid gap-4 xl:grid-cols-2">
                                <div className="rounded-lg border border-slate-200 bg-white p-4">
                                  <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Arbeitgeber-Checkliste</p>
                                  <ul className="mt-3 list-disc pl-5 text-sm text-slate-700">
                                    {filingData.employer_checklist?.map((entry) => (
                                      <li key={entry.title}>{entry.title} - {entry.status}</li>
                                    ))}
                                  </ul>
                                </div>
                                <div className="rounded-lg border border-slate-200 bg-white p-4">
                                  <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Bewerber-Checkliste</p>
                                  <ul className="mt-3 list-disc pl-5 text-sm text-slate-700">
                                    {filingData.candidate_checklist?.map((entry) => (
                                      <li key={entry.title}>{entry.title} - {entry.status}</li>
                                    ))}
                                  </ul>
                                </div>
                              </div>

                              <div className="grid gap-4 xl:grid-cols-2">
                                <div className="rounded-lg border border-slate-200 bg-white p-4">
                                  <div className="flex flex-wrap items-center justify-between gap-3">
                                    <div>
                                      <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Offene Filing-Aufgaben</p>
                                      <p className="mt-2 text-sm text-slate-600">
                                        Echte Arbeitsobjekte mit persistentem Status, Notiz und Candidate-Request-Schleife.
                                      </p>
                                    </div>
                                    <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-600">
                                      {filingData.task_board?.open_tasks?.length || 0} offen
                                    </span>
                                  </div>
                                  <div className="mt-3 space-y-3">
                                    {filingData.task_board?.open_tasks?.length ? filingData.task_board.open_tasks.slice(0, 10).map((task) => (
                                      <div key={`task-${task.task_id}`} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                        <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
                                          <div>
                                            <div className="flex flex-wrap items-center gap-2">
                                              <p className="font-semibold text-slate-900">{task.title}</p>
                                              <span className={`rounded-full border px-2 py-1 text-[11px] ${getResponsibilityMeta(task.owner_role).tone}`}>
                                                {task.owner_role}
                                              </span>
                                              <span className={`rounded-full border px-2 py-1 text-[11px] ${getReviewBucketMeta(task.source_bucket || "manual_only").tone}`}>
                                                {task.source_bucket || task.source_type}
                                              </span>
                                            </div>
                                            <p className="mt-1 text-sm text-slate-600">
                                              {formatTaskStatus(task.status)} · Prioritaet {task.priority || "normal"}
                                              {task.document_id ? ` · ${task.document_id}` : ""}
                                            </p>
                                            {task.description ? <p className="mt-2 text-sm text-slate-600">{task.description}</p> : null}
                                          </div>
                                          <div className="flex flex-wrap gap-2">
                                            <Button variant="outline" className="dashboard-inline-button" onClick={() => updateAuthorityTask(row.id, task, "complete")}>Erledigt</Button>
                                            <Button variant="outline" className="dashboard-inline-button" onClick={() => updateAuthorityTask(row.id, task, task.status === "blocked" ? "reopen" : "block")}>
                                              {task.status === "blocked" ? "Entblocken" : "Blockieren"}
                                            </Button>
                                            <Button variant="outline" className="dashboard-inline-button" onClick={() => updateAuthorityTask(row.id, task, "progress")}>In Arbeit</Button>
                                            {task.requires_candidate_input || task.owner_role === "candidate" ? (
                                              <Button variant="outline" className="dashboard-inline-button" onClick={() => createCandidateRequest(row.id, task, true)}>Request senden</Button>
                                            ) : null}
                                          </div>
                                        </div>
                                        <div className="mt-3 flex flex-col gap-2 lg:flex-row">
                                          <textarea
                                            className="min-h-[74px] flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none"
                                            placeholder="Completion-Notiz fuer diesen Task"
                                            value={detailState.taskNotes?.[String(task.task_id)] ?? task.completion_note ?? ""}
                                            onChange={(event) => handleTaskNoteChange(row.id, task.task_id, event.target.value)}
                                          />
                                          <Button variant="outline" className="dashboard-inline-button self-start" onClick={() => updateAuthorityTask(row.id, task, "note")}>
                                            Notiz speichern
                                          </Button>
                                        </div>
                                      </div>
                                    )) : (
                                      <p className="text-sm text-slate-600">Keine offenen Filing-Aufgaben mehr.</p>
                                    )}
                                  </div>
                                </div>

                                <div className="rounded-lg border border-slate-200 bg-white p-4">
                                  <div className="flex flex-wrap items-center justify-between gap-3">
                                    <div>
                                      <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Candidate Request Loop</p>
                                      <p className="mt-2 text-sm text-slate-600">
                                        Filing-spezifischer Request-Log fuer fehlende Angaben, Uploads, Uebersetzungen und Signaturen.
                                      </p>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                      <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-600">
                                        {filingData.candidate_request_board?.active_requests?.length || 0} aktiv
                                      </span>
                                      <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs text-amber-700">
                                        {filingData.candidate_request_board?.due_soon_count || 0} bald faellig
                                      </span>
                                      <span className="rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-xs text-rose-700">
                                        {filingData.candidate_request_board?.overdue_count || 0} ueberfaellig
                                      </span>
                                      <span className="rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs text-sky-700">
                                        {filingData.candidate_response_board?.pending_review_count || 0} in Review
                                      </span>
                                    </div>
                                  </div>
                                  <div className="mt-3 grid gap-2 md:grid-cols-2 xl:grid-cols-4">
                                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                      <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Awaiting candidate</p>
                                      <p className="mt-2 text-lg font-semibold text-slate-900">{filingData.candidate_request_board?.awaiting_candidate_count || 0}</p>
                                    </div>
                                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                      <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Awaiting review</p>
                                      <p className="mt-2 text-lg font-semibold text-slate-900">{filingData.candidate_request_board?.needs_review_count || 0}</p>
                                    </div>
                                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                      <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Stale candidate</p>
                                      <p className="mt-2 text-lg font-semibold text-slate-900">{filingData.candidate_request_board?.stale_candidate_count || 0}</p>
                                    </div>
                                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                      <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Fresh responses</p>
                                      <p className="mt-2 text-lg font-semibold text-slate-900">{filingData.candidate_request_board?.fresh_response_count || 0}</p>
                                    </div>
                                  </div>
                                  {filingData.candidate_request_board?.summary_lines?.length ? (
                                    <ul className="mt-3 list-disc pl-5 text-sm text-slate-700">
                                      {filingData.candidate_request_board.summary_lines.map((line) => (
                                        <li key={line}>{line}</li>
                                      ))}
                                    </ul>
                                  ) : null}
                                  {filingData.delivery_board ? (
                                    <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
                                      <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Delivery Audit</p>
                                      <p className="mt-2 text-sm text-slate-900">
                                        Mails gesendet {filingData.delivery_board.email_sent_count || 0} · Pending {filingData.delivery_board.email_pending_count || 0} · Fehler {filingData.delivery_board.delivery_failure_count || 0} · Retry {filingData.delivery_board.retry_count || 0}
                                      </p>
                                      {filingData.delivery_board.summary_lines?.length ? (
                                        <p className="mt-1 text-xs text-slate-500">{filingData.delivery_board.summary_lines.slice(0, 2).join(" · ")}</p>
                                      ) : null}
                                    </div>
                                  ) : null}
                                  {filingData.notification_board?.employer_notifications?.length ? (
                                    <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
                                      <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Letzte Filing-Hinweise</p>
                                      <div className="mt-2 space-y-2">
                                        {filingData.notification_board.employer_notifications.slice(0, 4).map((entry) => (
                                          <div key={`notification-${entry.notification_id}`} className="rounded-lg border border-white bg-white px-3 py-2 text-sm text-slate-600">
                                            <strong className="text-slate-900">{entry.title}</strong>
                                            <p className="mt-1">{entry.message}</p>
                                            <p className="mt-1 text-xs text-slate-500">{formatDateTime(entry.created_at)} · {entry.notification_type}</p>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  ) : null}
                                  <div className="mt-3 space-y-3">
                                    {filingData.candidate_request_board?.all_requests?.length ? filingData.candidate_request_board.all_requests.slice(0, 10).map((requestEntry) => (
                                      <div
                                        key={`request-${requestEntry.request_id}`}
                                        className={`rounded-lg border bg-slate-50 p-3 ${String(requestEntry.request_id) === String(requestedRequestId) ? "border-teal-300 ring-2 ring-teal-100" : "border-slate-200"}`}
                                      >
                                        <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
                                          <div>
                                            <p className="font-semibold text-slate-900">{requestEntry.title}</p>
                                            <p className="mt-1 text-sm text-slate-600">
                                              {formatRequestStatus(requestEntry.status)} · Dringlichkeit {formatUrgencyLabel(requestEntry.urgency || "medium")}
                                            </p>
                                            {requestEntry.response_status ? (
                                              <p className="mt-1 text-sm text-slate-600">{formatResponseStatus(requestEntry.response_status)}</p>
                                            ) : null}
                                            <div className="mt-2 flex flex-wrap gap-2">
                                              <span className={`rounded-full border px-2 py-1 text-[11px] ${getDeadlineTone(requestEntry)}`}>
                                                {requestEntry.overdue ? "ueberfaellig" : requestEntry.due_soon ? "bald faellig" : requestEntry.awaiting_employer_review ? "awaiting review" : "im Lauf"}
                                              </span>
                                              {requestEntry.stale_candidate ? (
                                                <span className="rounded-full border border-rose-200 bg-rose-50 px-2 py-1 text-[11px] text-rose-700">
                                                  candidate silent
                                                </span>
                                              ) : null}
                                              {requestEntry.stale_employer_review ? (
                                                <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] text-amber-700">
                                                  review pending
                                                </span>
                                              ) : null}
                                            </div>
                                            {requestEntry.request_reason ? (
                                              <p className="mt-2 text-sm text-slate-600">{requestEntry.request_reason}</p>
                                            ) : null}
                                            <p className="mt-2 text-xs text-slate-500">
                                              Faellig {formatDateTime(requestEntry.due_at)} · Reminder {formatDateTime(requestEntry.next_reminder_at)} · Letzte Aktivitaet {formatDateTime(requestEntry.last_activity_at)}
                                            </p>
                                            <p className="mt-1 text-xs text-slate-500">
                                              Offen seit {formatRelativeDays(requestEntry.open_days)} · Rest {formatRelativeDays(requestEntry.days_remaining)} · Reminder gesendet {requestEntry.reminder_count || 0}x
                                            </p>
                                            <p className="mt-1 text-xs text-slate-500">
                                              Letzte Mail {formatDateTime(requestEntry.delivery_summary?.last_email_delivery_at)} · Status {requestEntry.delivery_summary?.last_email_status || "in_app only / pending"} · Retry {requestEntry.delivery_summary?.retry_count || 0}
                                            </p>
                                            {requestEntry.delivery_summary?.last_email_error ? (
                                              <p className="mt-1 text-xs text-rose-600">Delivery-Fehler: {requestEntry.delivery_summary.last_email_error}</p>
                                            ) : null}
                                            {requestEntry.delivery_summary?.last_skip_reason ? (
                                              <p className="mt-1 text-xs text-slate-500">Mail-Fallback: {requestEntry.delivery_summary.last_skip_reason}</p>
                                            ) : null}
                                            {requestEntry.latest_response_summary?.preview_text ? (
                                              <div className="mt-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600">
                                                <strong className="text-slate-900">Letzte Candidate-Antwort:</strong> {requestEntry.latest_response_summary.preview_text}
                                              </div>
                                            ) : null}
                                            {requestEntry.latest_response_summary?.verification_reference_id ? (
                                              <p className="mt-2 text-xs text-slate-500">
                                                Verification Ref #{requestEntry.latest_response_summary.verification_reference_id}
                                              </p>
                                            ) : null}
                                          </div>
                                          <div className="flex flex-wrap gap-2">
                                            {requestEntry.status === "draft" ? (
                                              <Button variant="outline" className="dashboard-inline-button" onClick={() => updateCandidateRequest(row.id, requestEntry, "sent")}>Senden</Button>
                                            ) : null}
                                            {["sent", "viewed"].includes(requestEntry.status) ? (
                                              <Button
                                                variant="outline"
                                                className="dashboard-inline-button"
                                                onClick={() => updateCandidateRequest(row.id, requestEntry, "answered", { response_status: "response_received" })}
                                              >
                                                Antwort erhalten
                                              </Button>
                                            ) : null}
                                            {requestEntry.status === "answered" ? (
                                              <Button
                                                variant="outline"
                                                className="dashboard-inline-button"
                                                onClick={() => updateCandidateRequest(row.id, requestEntry, "answered", { response_status: "response_under_review" })}
                                              >
                                                In Review
                                              </Button>
                                            ) : null}
                                            {["answered", "viewed", "sent"].includes(requestEntry.status) ? (
                                              <Button
                                                variant="outline"
                                                className="dashboard-inline-button"
                                                onClick={() => updateCandidateRequest(row.id, requestEntry, "fulfilled", { response_status: "response_accepted" })}
                                              >
                                                Akzeptieren
                                              </Button>
                                            ) : null}
                                            {requestEntry.status === "answered" ? (
                                              <Button
                                                variant="outline"
                                                className="dashboard-inline-button"
                                                onClick={() => updateCandidateRequest(row.id, requestEntry, "answered", { response_status: "response_incomplete" })}
                                              >
                                                Unvollstaendig
                                              </Button>
                                            ) : null}
                                            {requestEntry.status === "answered" ? (
                                              <Button
                                                variant="outline"
                                                className="dashboard-inline-button"
                                                onClick={() => updateCandidateRequest(row.id, requestEntry, "answered", { response_status: "response_rejected" })}
                                              >
                                                Nacharbeit
                                              </Button>
                                            ) : null}
                                            {!["fulfilled", "obsolete", "expired"].includes(requestEntry.status) ? (
                                              <Button variant="outline" className="dashboard-inline-button" onClick={() => updateCandidateRequest(row.id, requestEntry, "obsolete")}>Obsolet</Button>
                                            ) : null}
                                          </div>
                                        </div>
                                      </div>
                                    )) : (
                                      <p className="text-sm text-slate-600">Noch keine Filing-Requests gespeichert.</p>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {filingData.form_prefill_guides?.length ? (
                                <div className="rounded-lg border border-slate-200 bg-white p-4">
                                  <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Vorbereitete Feldzuordnung / Pre-Fill Sheets</p>
                                  <div className="mt-3 space-y-3">
                                    {filingData.form_prefill_guides.map((guide) => (
                                      <div key={guide.document_id} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                        <div className="flex flex-wrap items-center justify-between gap-2">
                                          <p className="font-semibold text-slate-900">{guide.document_title}</p>
                                          <span className="rounded-full border border-slate-200 px-2 py-1 text-xs text-slate-500">{guide.preparation_mode}</span>
                                        </div>
                                        {guide.employer_review_note ? (
                                          <p className="mt-2 text-sm text-slate-600">{guide.employer_review_note}</p>
                                        ) : null}
                                        <div className="mt-3 space-y-3">
                                          {Object.entries(
                                            guide.prefill_fields.reduce((acc, field) => {
                                              const key = field.section || "Allgemein";
                                              if (!acc[key]) acc[key] = [];
                                              acc[key].push(field);
                                              return acc;
                                            }, {})
                                          ).map(([section, fields]) => (
                                            <div key={`${guide.document_id}-${section}`} className="rounded-lg border border-white bg-white p-3">
                                              <p className="text-xs uppercase tracking-[0.14em] text-slate-400">{section}</p>
                                              <ul className="mt-2 list-disc pl-5 text-sm text-slate-700">
                                                {fields.map((field) => (
                                                  <li key={`${guide.document_id}-${section}-${field.field_name}`}>
                                                    {field.field_name}: {field.target_value} · Quelle: {field.source_label} ({field.automation})
                                                    {field.required ? " · Pflichtfeld" : " · optional"}
                                                    {field.review_action ? ` · ${field.review_action}` : ""}
                                                    {field.manual_reason ? ` · ${field.manual_reason}` : ""}
                                                  </li>
                                                ))}
                                              </ul>
                                            </div>
                                          ))}
                                        </div>
                                        {guide.plausibility_warnings?.length ? (
                                          <ul className="mt-2 list-disc pl-5 text-sm text-amber-700">
                                            {guide.plausibility_warnings.map((warning) => (
                                              <li key={warning}>{warning}</li>
                                            ))}
                                          </ul>
                                        ) : null}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ) : null}

                              <div className="rounded-lg border border-slate-200 bg-white p-4">
                                <div className="flex items-center justify-between gap-3">
                                  <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Generierte Arbeitgeber-Unterlagen</p>
                                  <p className="text-xs text-slate-500">
                                    {filingData.generated_assets?.assets?.length || 0} Dateien im aktuellen Paket
                                  </p>
                                </div>
                                <div className="mt-3 grid gap-3 xl:grid-cols-2">
                                  {filingData.generated_assets?.assets?.length ? filingData.generated_assets.assets.map((asset) => (
                                    <div key={asset.asset_id} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                      <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
                                        <div>
                                          <p className="font-semibold text-slate-900">{asset.title}</p>
                                          <p className="mt-1 text-sm text-slate-600">{asset.asset_type} · {asset.format}</p>
                                        </div>
                                        <Button
                                          variant="outline"
                                          className="dashboard-inline-button"
                                          onClick={() => downloadPreparedAsset(row.id, asset)}
                                          disabled={detailState.exporting}
                                        >
                                          Vorbereitete Datei
                                        </Button>
                                      </div>
                                    </div>
                                  )) : (
                                    <p className="text-sm text-slate-600">Noch keine vorbereiteten Pack-Dateien vorhanden.</p>
                                  )}
                                </div>
                              </div>

                              <div className="rounded-lg border border-slate-200 bg-white p-4">
                                <div className="flex items-center justify-between gap-3">
                                  <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Persistente Kit-Historie</p>
                                  <p className="text-xs text-slate-500">BA {filingData.classification?.ba_consent_required ? "ja" : "nein"} · Vollmacht {filingData.classification?.power_of_attorney_required ? "ja" : "nein"}</p>
                                </div>
                                <div className="mt-3 space-y-3">
                                  {filingData.history?.length ? filingData.history.map((entry) => (
                                    <div key={`${entry.case_id}-${entry.version_no}`} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                      <div className="flex flex-wrap items-center justify-between gap-2">
                                        <p className="font-semibold text-slate-900">Version {entry.version_no} · {formatHistoryAction(entry.action_type)}</p>
                                        <span className="text-xs text-slate-500">{entry.created_at}</span>
                                      </div>
                                      <p className="mt-1 text-sm text-slate-600">
                                        {entry.classification?.procedure_explainer?.label || entry.classification?.procedure || "Pfad"} · {entry.classification?.federal_state || "ohne Bundesland"} · {entry.classification?.city || "ohne Stadt"}
                                      </p>
                                      {entry.completion_workflow ? (
                                        <p className="mt-1 text-xs text-slate-500">
                                          Completion: {entry.completion_workflow.progress_percent || 0}% · signierbar {entry.completion_workflow.sign_now_count || 0} · uploadbereit {entry.completion_workflow.done_now_count || 0} · Blocker {entry.completion_workflow.blocking_count || 0}
                                        </p>
                                      ) : null}
                                      <div className="mt-3 grid gap-2 md:grid-cols-3 xl:grid-cols-6">
                                        {Object.entries(entry.history_summary?.review_buckets || {}).map(([bucketKey, count]) => {
                                          const meta = getReviewBucketMeta(bucketKey);
                                          return (
                                            <div key={`${entry.version_no}-${bucketKey}`} className="rounded-lg border border-white bg-white p-2">
                                              <span className={`inline-flex rounded-full border px-2 py-1 text-[11px] ${meta.tone}`}>{meta.label}</span>
                                              <p className="mt-2 font-semibold text-slate-900">{count}</p>
                                            </div>
                                          );
                                        })}
                                      </div>
                                      <p className="mt-3 text-xs text-slate-500">
                                        Fillable PDFs: {entry.history_summary?.fillability?.fillable_pdf || 0} · Transferhilfen: {entry.history_summary?.fillability?.structured_transfer_only || 0} · Lokal geprueft: {entry.history_summary?.locally_checked_documents || 0} · Pack-Dateien: {entry.history_summary?.generated_assets_count || 0} · Offene Tasks: {entry.history_summary?.open_tasks_count || 0} · Aktive Requests: {entry.history_summary?.active_requests_count || 0} · Final Packs: {entry.history_summary?.final_pack_count || 0}
                                      </p>
                                      {entry.previous_version_delta ? (
                                        <div className="mt-3 rounded-lg border border-white bg-white p-3">
                                          <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Versionsvergleich zur Vorversion</p>
                                          <p className="mt-2 text-xs text-slate-500">
                                            Missing Docs {entry.previous_version_delta.missing_docs_delta >= 0 ? "+" : ""}{entry.previous_version_delta.missing_docs_delta} · Ready to upload {entry.previous_version_delta.ready_to_upload_delta >= 0 ? "+" : ""}{entry.previous_version_delta.ready_to_upload_delta} · Ready to sign {entry.previous_version_delta.ready_to_sign_delta >= 0 ? "+" : ""}{entry.previous_version_delta.ready_to_sign_delta} · Fillable PDFs {entry.previous_version_delta.fillable_pdf_delta >= 0 ? "+" : ""}{entry.previous_version_delta.fillable_pdf_delta} · Tasks {entry.previous_version_delta.open_tasks_delta >= 0 ? "+" : ""}{entry.previous_version_delta.open_tasks_delta} · Requests {entry.previous_version_delta.active_requests_delta >= 0 ? "+" : ""}{entry.previous_version_delta.active_requests_delta} · Final Packs {entry.previous_version_delta.final_pack_delta >= 0 ? "+" : ""}{entry.previous_version_delta.final_pack_delta}
                                          </p>
                                          {entry.previous_version_delta.summary_lines?.length ? (
                                            <ul className="mt-2 list-disc pl-5 text-sm text-slate-700">
                                              {entry.previous_version_delta.summary_lines.map((line) => (
                                                <li key={`${entry.version_no}-${line}`}>{line}</li>
                                              ))}
                                            </ul>
                                          ) : null}
                                          <div className="mt-3 grid gap-3 xl:grid-cols-3">
                                            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                              <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Dokumente</p>
                                              <p className="mt-2 text-xs text-slate-500">+ {entry.previous_version_delta.document_changes?.added?.length || 0} · - {entry.previous_version_delta.document_changes?.removed?.length || 0}</p>
                                              <ul className="mt-2 list-disc pl-5 text-xs text-slate-600">
                                                {(entry.previous_version_delta.document_changes?.added || []).slice(0, 3).map((label) => (
                                                  <li key={`${entry.version_no}-doc-add-${label}`}>Neu: {label}</li>
                                                ))}
                                                {(entry.previous_version_delta.document_changes?.removed || []).slice(0, 3).map((label) => (
                                                  <li key={`${entry.version_no}-doc-rem-${label}`}>Entfallen: {label}</li>
                                                ))}
                                              </ul>
                                            </div>
                                            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                              <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Missing Docs</p>
                                              <p className="mt-2 text-xs text-slate-500">+ {entry.previous_version_delta.missing_document_changes?.added?.length || 0} · - {entry.previous_version_delta.missing_document_changes?.removed?.length || 0}</p>
                                              <ul className="mt-2 list-disc pl-5 text-xs text-slate-600">
                                                {(entry.previous_version_delta.missing_document_changes?.added || []).slice(0, 3).map((label) => (
                                                  <li key={`${entry.version_no}-missing-add-${label}`}>Neu: {label}</li>
                                                ))}
                                                {(entry.previous_version_delta.missing_document_changes?.removed || []).slice(0, 3).map((label) => (
                                                  <li key={`${entry.version_no}-missing-rem-${label}`}>Geschlossen: {label}</li>
                                                ))}
                                              </ul>
                                            </div>
                                            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                              <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Behoerden</p>
                                              <p className="mt-2 text-xs text-slate-500">+ {entry.previous_version_delta.authority_changes?.added?.length || 0} · - {entry.previous_version_delta.authority_changes?.removed?.length || 0}</p>
                                              <ul className="mt-2 list-disc pl-5 text-xs text-slate-600">
                                                {(entry.previous_version_delta.authority_changes?.added || []).slice(0, 3).map((label) => (
                                                  <li key={`${entry.version_no}-authority-add-${label}`}>Neu: {label}</li>
                                                ))}
                                                {(entry.previous_version_delta.authority_changes?.removed || []).slice(0, 3).map((label) => (
                                                  <li key={`${entry.version_no}-authority-rem-${label}`}>Entfallen: {label}</li>
                                                ))}
                                              </ul>
                                            </div>
                                          </div>
                                        </div>
                                      ) : null}
                                    </div>
                                  )) : (
                                    <p className="text-sm text-slate-600">Noch keine gespeicherte Kit-Historie vorhanden.</p>
                                  )}
                                </div>
                              </div>
                            </div>
                          ) : null}
                            </>
                          ) : null}
                        </div>

                        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                            <div>
                              <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Interne Entscheidungsvorbereitung</p>
                              <p className="mt-2 text-slate-600">
                                Nutzt den bestehenden Bewerbungsstatus plus kurze interne Notiz. Keine Team-Workflows, kein Ranking.
                              </p>
                            </div>
                            <p className="text-xs text-slate-400">
                              Zuletzt aktualisiert: {detail.decision_preparation?.updated_at || "n/a"}
                            </p>
                          </div>

                          <div className="mt-4 grid gap-4 lg:grid-cols-[220px,1fr]">
                            <div>
                              <label className="mb-2 block text-xs uppercase tracking-[0.18em] text-slate-400">
                                Decision State
                              </label>
                              <Select
                                value={detailState.form.decision_state}
                                onValueChange={(value) => handleDecisionFormChange(row.id, "decision_state", value)}
                              >
                                <SelectTrigger className="dashboard-field">
                                  <SelectValue placeholder="Status waehlen" />
                                </SelectTrigger>
                                <SelectContent>
                                  {DECISION_STATE_OPTIONS.map((option) => (
                                    <SelectItem key={option.value} value={option.value}>
                                      {option.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            <div>
                              <label className="mb-2 block text-xs uppercase tracking-[0.18em] text-slate-400">
                                Interne Notiz
                              </label>
                              <textarea
                                value={detailState.form.decision_note}
                                onChange={(event) => handleDecisionFormChange(row.id, "decision_note", event.target.value)}
                                maxLength={500}
                                rows={4}
                                placeholder="Kurze interne Einordnung zur Entscheidungsvorbereitung."
                                className="dashboard-field dashboard-textarea w-full"
                              />
                              <p className="mt-1 text-right text-xs text-slate-500">
                                {detailState.form.decision_note.length}/500
                              </p>
                            </div>
                          </div>

                          <div className="mt-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                            <p className="text-xs text-slate-400">
                              Sichtgrenze: keine Rohdokumente, keine Dokumentpfade, keine Review-Metadaten, keine Suspicion-Details.
                            </p>
                            <Button
                              className="dashboard-action-button"
                              onClick={() => saveDecisionPreparation(row.id)}
                              disabled={detailState.saving}
                            >
                              {detailState.saving ? "Speichert..." : "Decision Prep speichern"}
                            </Button>
                          </div>

                          {detailState.flash ? <p className="mt-3 text-sm text-emerald-700">{detailState.flash}</p> : null}
                        </div>
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            );
          })}
        </CardContent>
      </Card>
    </CockpitShell>
  );
};

export default StbApplications;
